import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AdminIntroStatusItem } from 'src/pages/skillManagementAdmin/types';
import type { StoreItemType } from 'src/pages/storeAdmin/constants';
import axios from 'axios';
import type { SkillModuleTodosResponse } from 'src/redux/types/skillTodos';
import type {
  ApnEmployerDataScraperBody,
  ApnEmployerDataScraperV2Body,
  EmployerDataScraperBody,
  RecruiterNormalisationBody,
  AdminSendMobileAppNotificationBody,
  AdminCreateChatTemplateBody,
  AdminUpdateJobBody,
  AdminUserUpdateBody,
  BulkTemplateSupportBody,
  CreateAdminMockTestBody,
  GetEmailGroupLogsListParam,
  GenerateSkillTestBody,
  JobProfileMatchList,
  PremiumPromoVariant,
  SendMsgOrgOwnerBody,
  SentEmailBody,
  SendEmployerEmailBody,
  SendFollowUpBody,
  UpdateMockTestBody,
  WhatsappMarketingNotificationSuccessBody,
  CreateLevelBody,
  UpdateLevelBody,
  CreateBadgeBody,
  CreateDashboardBannerBody,
  ReorderDashboardBannersBody,
  UpdateDashboardBannerBody,
  GetAllBadgesSub,
  AddNewStoreItemBody,
  AdminExcludeFromInvoiceSeriesBody,
  AdminUpdateTrasnsactionBody,
  CreatePotentialRecruiterBody,
  PotentialRecruiterParameter,
  UpdateJobRoleBody,
  CreateCollegeBody,
  CreateCompanyBody,
  CreateExamBody,
  CreateSkillBody,
  UpdateSkillBody,
  CreateJobRoleBody,
  CreateDegreeBody,
  UpdateDegreeBody,
  CreateSpecializationBody,
  UpdateSpecializationBody,
  GetPotentialApplicantsBody,
  GetPotentialApplicantsResponse,
  CreatePotentialApplicantsBody,
  ImportPotentialApplicantListMembersBody,
  SuccessResponseBase,
  CreateCouponBody,
  UpdateEmailGroupAttributesParam,
  GetEmailSettingsResponse,
  UpdateEmailSettingsBody,
  GetUnderReviewParams,
  ToggleGuidedCourseBody,
  ReviewJobParam,
  StartCourseGenerationBody,
  GetUserListParams,
  PaymentApiGetAllItemsAdminAvailableToEnum,
  PaymentApiGetAllItemsAdminItemTypeEnum,
  CourseBuildHierarchyResponse,
  UserResumeList,
  SkillApiListAdminCourseFeedbackScopeEnum,
  SendUserMailTestBodyCategoryEnum,
  SendUserMailTestBodyCourseTemplateEnum,
  SendUserMailTestBodyCompetitionTemplateEnum,
  SendUserMailTestBodySecondaryCategoriesEnum,
  CreateMailTemplateBodySub,
  GetOrgCsvParam,
  StatisticsApiGetAdminPremiumSubscriptionStatisticsIntroOfferEnum,
} from 'src/openapi';
import type { PromotionEmailPayload } from 'src/helpers/apiClient';
import apiClient from 'src/helpers/apiClient';
import { enqueueSnackbar } from 'notistack';
import { getFormattedErr } from 'src/helpers';
import type { ActivityFilter } from 'src/constants/activityFilters';
import type {
  FreeUnlockAudienceRules,
  FreeUnlockEmailReachParams,
  FreeUnlockOfferInput,
} from 'src/redux/types/employerCredit';
import ConfigUrl from '../../../config';
import type {
  JobReviewFilter,
  OrgReviewFilters,
  ReferralSummaryFilters,
  ReviewJobExternalParam,
  SkillStatisticsFilters,
  SkillStatisticsResponse,
  UserMgmtFilter,
} from '../types/admin';

export const getAdminJobPostedStatistics = createAsyncThunk(
  'get/job-posted-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminJobPostedStatistics({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAdminExternalJobStatistics = createAsyncThunk(
  'get/external-job-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType?: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      // Custom apiClient method until the typed client is regenerated from spec.
      const data = await apiClient.getAdminExternalJobStatistics({
        start_date: startDate,
        end_date: endDate,
        granularity,
        platform,
      });
      return data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAdminCreditsStatistics = createAsyncThunk(
  'get/credits-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminCreditsStatistics({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAdminGemsStatistics = createAsyncThunk(
  'get/gems-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminGemsStatistics({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAdminPlacementPartnerStatistics = createAsyncThunk(
  'get/placement-partner-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminPlacementPartnerStatistics({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAdminJobStatistics = createAsyncThunk(
  'get/job-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminJobStatistics({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAdminQuestionAttemptStatistics = createAsyncThunk(
  'get/question-attempt-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminQuestionStatistics({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAdminQuestionCreatedStatistics = createAsyncThunk(
  'get/question-created-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminQuestionCreatedStatistics({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAdminReferralStatistics = createAsyncThunk(
  'get/referral-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminReferralStatistics({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAdminResumeStatistics = createAsyncThunk(
  'get/resume-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminResumeStatistics({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAdminSkillTestStatistics = createAsyncThunk(
  'get/skill-test-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminSkillTestStatistics({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAdminChatStatistics = createAsyncThunk(
  'get/chat-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminChatStatistics({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAdminJobChatStatistics = createAsyncThunk(
  'get/job-chat-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminJobChatStatistics({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAdminPreJobAnalysisStatistics = createAsyncThunk(
  'get/pre-job-analysis-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminPreJobAnalysisStatistics({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAdminAiGuideStatistics = createAsyncThunk(
  'get/ai-guide-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminAiGuideStatistics({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAdminCompetitionStatistics = createAsyncThunk(
  'get/competition-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminCompetitionStatistics({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAdminEmployerActivityStatistics = createAsyncThunk(
  'get/employer-activity-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetEmployerActivity({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAdminAlgoLogsStatistics = createAsyncThunk(
  'get/algo-logs-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAlgoLogsActivity({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAdminAiUsageStatistics = createAsyncThunk(
  'get/ai-usage-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminAiUsageStatistics({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAdminPlayArenaStatistics = createAsyncThunk(
  'get/play-arena-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminPlayArenaStatistics({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAdminJobInviteStatistics = createAsyncThunk(
  'get/job-invite-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      jobId,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      jobId?: string;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminJobProfileInvitesStatistics({
        endDate,
        granularity,
        platform,
        jobId,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAdminLearnerActivityStatistics = createAsyncThunk(
  'get/learner-activity-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminLearnerActivityStatistics({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAdminOtpFunnelStatistics = createAsyncThunk(
  'get/otp-funnel-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminOtpFunnelStatistics({
        endDate,
        granularity,
        platform,
        startDate,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAdminPaymentFunnelStatistics = createAsyncThunk(
  'get/payment-funnel-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType?: string;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminPaymentFunnelStatistics({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAdminPremiumSubscriptionStatistics = createAsyncThunk(
  'get/premium-subscription-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
      introOffer,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType?: string;
      platform?: string;
      /** Only mandates on this intro offer; absent: every mandate. */
      introOffer?: StatisticsApiGetAdminPremiumSubscriptionStatisticsIntroOfferEnum;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminPremiumSubscriptionStatistics({
        endDate,
        granularity,
        introOffer,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getCreditActionTypeStatistics = createAsyncThunk(
  'get/credit-action-type',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminCreditsStatisticsActionType({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getReviewOrgs = createAsyncThunk(
  'get/review-organisations',
  async (
    {
      isDoc,
      filters,
      page,
      pageSize,
      status,
      searchQuery,
      sortOrder,
    }: {
      isDoc: boolean | undefined;
      filters: OrgReviewFilters;
      page: number;
      pageSize: number;
      status: string | undefined;
      searchQuery: string | undefined;
      sortOrder: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getOrgApi()
      ).organizationApiReviewOrganizations(
        {
          document: isDoc,
          endDate: filters.endDate,
          isPlacementPartner: undefined, // admin org-review covers all orgs
          maxJobPosted: filters.maxJobsPosted,
          minActivityCount: filters.minActivityCount,
          minApplicants: filters.minApplicants,
          minJobPosted: filters.minJobsPosted,
          orgId: undefined,
          page: page.toString(),
          pageSize: pageSize.toString(),
          planType: filters.planType,
          searchQuery,
          sortBy: undefined,
          sortOrder,
          startDate: filters.startDate,
          status,
        },
        // Not in the generated client until the spec is refreshed; axios appends
        // `params` to the query string the client built.
        {
          params:
            filters.freeUnlockOfferId && filters.freeUnlock
              ? {
                  free_unlock_offer_id: filters.freeUnlockOfferId,
                  free_unlock: filters.freeUnlock,
                }
              : undefined,
        },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getOrgsDetailsById = createAsyncThunk(
  'get/reviewOrgById',
  async (
    {
      orgId,
    }: {
      orgId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getOrgApi()
      ).organizationApiReviewOrganizations({
        document: undefined,
        endDate: undefined,
        isPlacementPartner: undefined,
        maxJobPosted: undefined,
        minActivityCount: undefined,
        minApplicants: undefined,
        minJobPosted: undefined,
        orgId,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
let reviewJobsListAbortController: AbortController | null = null;

export const getReviewJobs = createAsyncThunk(
  'get/review-jobs',
  async (
    {
      page,
      pageSize,
      status,
      searchQuery,
      orgStatus,
      filters,
      isGptJob,
      notificationsEnabled,
      associate_with_competitions,
      isReviewPending,
      excludeExpired,
      sortBy,
      sortOrder,
    }: {
      page: number;
      pageSize: number;
      status: string | undefined;
      searchQuery: string | undefined;
      orgStatus?: string;
      filters: JobReviewFilter;
      isGptJob?: boolean;
      notificationsEnabled?: boolean;
      associate_with_competitions?: boolean;
      isReviewPending?: boolean;
      excludeExpired?: boolean;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    },
    { rejectWithValue },
  ) => {
    // Cancel any still-pending review-jobs request so a filter change doesn't
    // leave a stale request in flight racing with the latest one.
    reviewJobsListAbortController?.abort();
    const controller = new AbortController();
    reviewJobsListAbortController = controller;

    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiReviewJobs(
        {
          reviewJobParam: {
            status,
            start_date: filters.startDate,
            end_date: filters.endDate,
            job_type: filters.jobType ? [filters.jobType] : [],
            salary_min: filters.salaryMin?.toString(),
            salary_max: filters.salaryMax?.toString(),
            user_search_query: searchQuery,
            min_experience: filters.minExperience?.toString(),
            max_experience: filters.maxExperience?.toString(),
            job_role_ids: filters.jobRole?.id,
            skills_filter: filters.skills?.map((s) => s.id).join(','),
            location_ids: filters.locations?.length
              ? filters.locations
                  .map((l) => l.place_id)
                  .filter(Boolean)
                  .join(',')
              : undefined,
            company_size: filters.companySize,
            work_flexibility_types: filters.workPlace,
            sort_by: sortBy ?? 'date',
            sort_order: sortOrder ?? 'desc',
            page: page.toString(),
            page_size: pageSize.toString(),
            org_status: orgStatus,
            is_gpt_job: isGptJob,
            min_logs_count: filters.minLogs,
            max_logs_count: filters.maxLogs,
            notifications_enabled: notificationsEnabled,
            min_applicant_count: filters.minApplicant,
            max_applicant_count: filters.maxApplicant,
            associate_with_competitions,
            is_review_pending: isReviewPending,
            exclude_expired: excludeExpired,
            org_id: filters.org?.id,
            is_external:
              filters.jobSource === undefined
                ? undefined
                : filters.jobSource === 'external',
            sources: filters.atsSources?.length
              ? filters.atsSources.join(',')
              : undefined,
            ats_board_ids: filters.atsBoard?.id,
            board_category: filters.boardCategory,
            claude_reviewed: filters.claudeReviewed,
            min_content_grade: filters.minContentGrade,
            max_content_grade: filters.maxContentGrade,
            min_promotability_score: filters.minPromotability,
            max_promotability_score: filters.maxPromotability,
            is_promotability_scored: filters.isPromotabilityScored,
            is_third_party_hiring: filters.isThirdPartyHiring,
            is_enriched: filters.isEnriched,
          } as ReviewJobParam & ReviewJobExternalParam,
        },
        { signal: controller.signal },
      );
      if (reviewJobsListAbortController === controller) {
        reviewJobsListAbortController = null;
      }
      return response.data;
    } catch (err: any) {
      if (reviewJobsListAbortController === controller) {
        reviewJobsListAbortController = null;
      }
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') {
        return rejectWithValue({ cancelled: true });
      }
      return rejectWithValue(err.response.data);
    }
  },
);
export const getReviewJobById = createAsyncThunk(
  'get/review-job-by-id',
  async (
    {
      jobId,
    }: {
      jobId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiReviewJobs({
        reviewJobParam: {
          job_id: jobId,
        },
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getOrgActivityLogs = createAsyncThunk(
  'get/OrgActivity',
  async (
    {
      maxDate,
      minDate,
      orgId,
      pageNo,
      pageSize,
    }: {
      maxDate: string;
      minDate: string;
      orgId: string;
      pageNo: number;
      pageSize: number;
    },
    { rejectWithValue },
  ) => {
    const orgArr = [orgId];
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetSystemActivityLogs({
        activityTypeList: undefined,
        applicantId: undefined,
        jobIds: undefined,
        maxDate,
        minDate,
        orgIds: orgArr,
        pageNo,
        pageSize,
        userId: undefined,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getJobReports = createAsyncThunk(
  'get/job-reports',
  async ({ jobId }: { jobId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetJobReports({ jobId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAdminPendingCounts = createAsyncThunk(
  'get/admin-pending-counts',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getManagementApi()
      ).managementApiGetPendingEntitiesCounts();
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  },
);
export const getJobActivityLogs = createAsyncThunk(
  'get/OrgActivity',
  async (
    {
      maxDate,
      minDate,
      jobId,
      pageNo,
      pageSize,
    }: {
      maxDate: string;
      minDate: string;
      jobId: string;
      pageNo: number;
      pageSize: number;
    },
    { rejectWithValue },
  ) => {
    const jobArr = [jobId];
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetSystemActivityLogs({
        activityTypeList: undefined,
        applicantId: undefined,
        jobIds: jobArr,
        maxDate,
        minDate,
        orgIds: undefined,
        pageNo,
        pageSize,
        // No userType filter — show ALL activity on the job (applicant-, employer-
        // and system-facing), not just employer-performed actions.
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getUserActivityLogs = createAsyncThunk(
  'get/OrgActivity',
  async (
    {
      maxDate,
      minDate,
      userId,
      pageNo,
      pageSize,
    }: {
      maxDate?: string;
      minDate?: string;
      userId: string;
      pageNo: number;
      pageSize: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetSystemActivityLogs({
        activityTypeList: undefined,
        applicantId: undefined,
        jobIds: undefined,
        maxDate,
        minDate,
        orgIds: undefined,
        pageNo,
        pageSize,
        userId,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getUserMailLogs = createAsyncThunk(
  'get/mailLogs',
  async (
    {
      maxDate,
      minDate,
      userEmail,
      pageNo,
      pageSize,
    }: {
      maxDate: string;
      minDate: string;
      userEmail: string;
      pageNo: number;
      pageSize: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getMailAPi()
      ).mailApiGetMailsLogs({
        maxDate,
        minDate,
        pageNo,
        pageSize,
        recipient: userEmail,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const markOrgAsPremium = createAsyncThunk(
  'put/updateOrgPremium',
  async (
    { orgId, premiumEndDate }: { orgId: string; premiumEndDate: string },
    { rejectWithValue },
  ) => {
    const body = {
      post_premium_job_subscription_expires_on: premiumEndDate,
    };
    try {
      const response = await (
        await apiClient.getOrgApi()
      ).organizationApiUpdateOrganizationToPremium({
        orgId,
        updateOrgToPremiumBody: body,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const bulkOrgsStatusUpdate = createAsyncThunk(
  'put/updateBulkStatus',
  async (
    {
      orgIds,
      rejectReason,
      status,
    }: { orgIds: string[]; rejectReason: string; status: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getOrgApi()
      ).organizationApiBulkUpdateOrganizationStatus({
        bulkUpdateOrgBody: {
          org_ids: orgIds,
          rejection_reason: rejectReason,
          status,
        },
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const bulkJobStatusUpdate = createAsyncThunk(
  'put/updateJobBulkStatus',
  async (
    {
      jobIds,
      rejectReason,
      status,
      rating,
    }: {
      jobIds: string[];
      rejectReason?: string;
      status?: string;
      rating: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiBulkJobStatusUpdate({
        bulkStatusUpdateBody: {
          job_ids: jobIds,
          rejection_reason: rejectReason,
          status,
          rating,
        },
      });
      enqueueSnackbar('Status Updated Successfully', {
        variant: 'success',
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const adminAddOrgCredits = createAsyncThunk(
  'admin/addOrgCredits',
  async (
    { orgId, amount }: { orgId: string; amount: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getEmployerCreditApi()
      ).employerCreditApiAdminAddEmployerCredits({
        orgId,
        adminAddCreditsBody: { amount },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteJobAdmin = createAsyncThunk(
  'delete/Job',
  async (
    {
      jobId,
      orgId,
    }: {
      jobId: string;
      orgId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiDeleteJob({ jobId, orgId });
      enqueueSnackbar('Job deleted successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));

      return rejectWithValue(err.response.data);
    }
  },
);
export const bulkOrgsSendMessage = createAsyncThunk(
  'put/orgBulkSendMessage',
  async (sendMsgOrgOwnerBody: SendMsgOrgOwnerBody, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiSendMsgOrgOwner({ sendMsgOrgOwnerBody });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const reminderOrgMail = createAsyncThunk(
  'send/reminderMail',
  async (
    { orgId, status }: { orgId: string; status: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getOrgApi()
      ).organizationApiTriggerDocumentStatusMail({ orgId, status });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getOrgContactDetails = createAsyncThunk(
  'get/orgContactDetails',
  async (
    {
      attribute,
      orgId,
      orgUserId,
    }: { attribute: string; orgId: string; orgUserId?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getOrgApi()
      ).organizationApiGetOrganizationContactDetails({
        attribute,
        orgId,
        orgUserId,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const changeNotificationStatusChange = createAsyncThunk(
  'put/notification',
  async (
    {
      notification,
      orgId,
      jobId,
    }: { notification: boolean; orgId: string; jobId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiUpdateJobAdmin({
        adminUpdateJobBody: {
          notifications_enabled: notification,
          org_id: orgId,
          job_id: jobId,
        },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getRelatedMocktest = createAsyncThunk(
  'get/mocktest',
  async (jobId: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetJobRelatedMocktests({ jobId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getUserToken = createAsyncThunk(
  'get/UserToken',
  async ({ email }: { email: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getTokenApi()
      ).tokenApiGetTokenByUserDetails({ query: email });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const rebuildMatchingProfile = createAsyncThunk(
  'get/rebuild',
  async (jobIds: string[], { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiRebuildMatchingProfiles({
        reBuildJobMatchingProfiles: { job_ids: jobIds },
      });
      enqueueSnackbar('Rebuild Successfully', {
        variant: 'success',
      });

      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const reprocessStuckApplicantResumes = createAsyncThunk(
  'admin/reprocessStuckApplicantResumes',
  async (
    {
      jobIds = [],
      includeRecoverableFailed = false,
    }: { jobIds?: string[]; includeRecoverableFailed?: boolean },
    { rejectWithValue },
  ) => {
    try {
      // ids scopes to specific jobs (empty => all jobs); include_recoverable_failed also
      // re-queues Failed-but-already-parsed candidates (backlog recovery).
      const response = await (
        await apiClient.getJobApi()
      ).jobApiAdminReprocessStuckResumes({
        reprocessStuckResumesBody: {
          ids: jobIds,
          include_recoverable_failed: includeRecoverableFailed,
        },
      });
      enqueueSnackbar(response.data?.message || 'Resume re-processing queued', {
        variant: 'success',
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAdminMailTemplateById = createAsyncThunk(
  'get/templateById',
  async (tempId: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getMailAPi()
      ).mailApiGetTemplateById({ templateId: tempId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const postEmailAttachment = createAsyncThunk(
  'post/emailAttachment',
  async (file: File, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getFilesApi()
      ).filesApiUploadAttachmentToBucket({ file });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const sendEmail = createAsyncThunk(
  'post/Email',
  async (data: SentEmailBody, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getMailAPi()
      ).mailApiSendEmailApi({ sentEmailBody: data });
      if (data.email_counts_only !== true) {
        enqueueSnackbar('Email queued for delivery', {
          variant: 'success',
        });
      }
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const sendEmployerEmail = createAsyncThunk(
  'post/EmployerEmail',
  async (data: SendEmployerEmailBody, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getMailAPi()
      ).mailApiSendEmployerEmailApi({ sendEmployerEmailBody: data });
      if (data.email_counts_only !== true) {
        enqueueSnackbar('Email queued for delivery', {
          variant: 'success',
        });
      }
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const deleteEmailTemplate = createAsyncThunk(
  'delete/email-template',
  async (templateId: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getMailAPi()
      ).mailApiDeleteTemplateById({ templateId });
      enqueueSnackbar('Template Deleted Successfully', {
        variant: 'success',
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const createEmailTemplate = createAsyncThunk(
  'post/email-template',
  async (
    {
      templateName,
      createTemplateDetails,
      attachmentUrls,
    }: {
      templateName: string;
      createTemplateDetails: CreateMailTemplateBodySub[];
      attachmentUrls: string[] | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getMailAPi()
      ).mailApiCreateTemplate({
        createMailTemplateBody: {
          template_name: templateName,
          template_details: createTemplateDetails,
          template_args: {},
          attachment_urls: attachmentUrls,
        },
      });
      enqueueSnackbar('Template Created Successfully', {
        variant: 'success',
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const updateEmailTemplate = createAsyncThunk(
  'put/email-template',
  async (
    {
      templateName,
      updateTemplateDetails,
      attachmentUrls,
      templateId,
    }: {
      templateName: string;
      updateTemplateDetails: CreateMailTemplateBodySub[];
      attachmentUrls: string[] | null;
      templateId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getMailAPi()
      ).mailApiUpdateTemplate({
        templateId,
        createMailTemplateBody: {
          template_name: templateName,
          template_details: updateTemplateDetails,
          template_args: {},
          attachment_urls: attachmentUrls,
        },
      });
      enqueueSnackbar('Template Updated Successfully', {
        variant: 'success',
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const reminderMatchingProfile = createAsyncThunk(
  'send/reminder',
  async (
    {
      jobId,
      reminderCount,
    }: {
      jobId: string;
      reminderCount: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiReminderEmailAllMatchingJobProfiles({
        jobId,
        userResumeEntityListRemainderCount: {
          reminder_count: reminderCount,
        },
      });
      enqueueSnackbar('Reminder Email sent Successfully', {
        variant: 'success',
      });

      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));

      return rejectWithValue(err.response.data);
    }
  },
);
export const getAdminSkills = createAsyncThunk(
  'get/admin-skills',
  async (
    {
      query,
      status = 'Accepted',
      sortBy = 'name',
      sortOrder = 'asc',
      showJobAssociatedOnly = false,
    }: {
      query: string;
      status?: string;
      sortBy?: string;
      sortOrder?: string;
      showJobAssociatedOnly?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiAdminGetJobSkills({
        name: query,
        pageNo: undefined,
        pageSize: undefined,
        showJobAssociatedSkills: showJobAssociatedOnly,
        sortBy,
        sortOrder,
        status,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getEmailTemplates = createAsyncThunk(
  'get/email-templates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getMailAPi()
      ).mailApiGetAllTemplates();
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getCompetitionAdmin = createAsyncThunk(
  'competition/getCompetition',
  async (
    {
      page = 1,
      pageSize = 10,
      searchQuery,
    }: { page?: number; pageSize?: number; searchQuery?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiGetAllCompetitions({
        isRegistered: undefined,
        page: String(page),
        pageSize: String(pageSize),
        searchQuery,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const rejectUnreject = createAsyncThunk(
  'put/reject-unreject',
  async (
    {
      jobId,
      reject,
      users,
      resumes,
    }: {
      jobId: string;
      reject: boolean;
      users?: string[];
      resumes?: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiRejectJobProfile({
        jobId,
        userResumeEntityList: {
          resume_entity_ids: resumes,
          user_ids: users,
          status: reject,
        },
      });
      enqueueSnackbar(
        reject ? 'Rejected successfully' : 'Unrejected successfully',
        { variant: 'success' },
      );
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));

      return rejectWithValue(err.response.data);
    }
  },
);
export const sendReminderMatchingProfile = createAsyncThunk(
  'send/reminder',
  async (
    {
      jobId,
      users,
      resumes,
    }: {
      jobId: string;
      users?: string[];
      resumes?: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiEmailAllMatchingJobProfiles({
        jobId,
        userResumeEntityList: {
          resume_entity_ids: resumes,
          user_ids: users,
        },
      });
      enqueueSnackbar('Email sent successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));

      return rejectWithValue(err.response.data);
    }
  },
);
export const updateJobDetailsAdmin = createAsyncThunk(
  'put/job-deatils',
  async (
    {
      jobData,
    }: {
      jobData: AdminUpdateJobBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiUpdateJobAdmin({ adminUpdateJobBody: jobData });
      enqueueSnackbar('Job Edited Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const getMatchingProfile = createAsyncThunk(
  'get/matchingUsers',
  async (
    {
      jobId,
      endDate,
      page,
      rowsPerPage,
      searchQuery,
      sortBy,
      sortOrder,
      startDate,
      autoApplyEnabled,
      hasApplied,
      minExperience,
      maxExperience,
      skills,
      jobRole,
    }: {
      jobId: string;
      endDate: string | undefined;
      page: number;
      rowsPerPage: number;
      searchQuery: string | undefined;
      sortBy: string;
      sortOrder: string;
      autoApplyEnabled?: boolean;
      hasApplied?: boolean;
      startDate: string | undefined;
      maxExperience?: number;
      minExperience?: number;
      skills?: string[];
      jobRole?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetJobMatchingUsers({
        jobId,
        autoApplyEnabled,
        endDate,
        hasApplied,
        jobRoleList: jobRole ? [jobRole] : undefined,
        maxExperience,
        minExperience,
        notified: undefined,
        pageNo: page,
        pageSize: rowsPerPage,
        searchQuery,
        skillIds: skills,
        sortBy,
        sortOrder,
        startDate,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getMatchingResumes = createAsyncThunk(
  'get/matchingResumes',
  async (
    {
      jobId,
      endDate,
      page,
      rowsPerPage,
      searchQuery,
      sortBy,
      sortOrder,
      startDate,
    }: {
      jobId: string;
      endDate: string | undefined;
      page: number;
      rowsPerPage: number;
      searchQuery: string | undefined;
      sortBy: string;
      sortOrder: string;
      startDate: string | undefined;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetJobMatchingEntities({
        jobId,
        autoApplyEnabled: undefined,
        endDate,
        hasApplied: undefined,
        jobRoleList: undefined,
        maxExperience: undefined,
        minExperience: undefined,
        notified: undefined,
        pageNo: page,
        pageSize: rowsPerPage,
        searchQuery,
        skillIds: undefined,
        sortBy,
        sortOrder,
        startDate,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const downloadMetaData = createAsyncThunk(
  'get/GoogleAdsMetadataCsv',
  async (
    {
      endDate,
      startDate,
      name,
      isDocument,
      status,
      maxJobPosted,
      minJobPosted,
      minApplicants,
      planType,
      freeUnlockOfferId,
      freeUnlock,
    }: {
      endDate?: string;
      startDate?: string;
      name?: string;
      isDocument?: boolean;
      status?: string;
      maxJobPosted?: number;
      minJobPosted?: number;
      minApplicants?: number;
      planType?: string;
      // The Review Orgs offer filter; only sent as a pair.
      freeUnlockOfferId?: string;
      freeUnlock?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetJobsGoogleAdsMetadataCsv({
        getOrgCsvParam: {
          document: isDocument,
          end_date: endDate,
          max_job_posted: maxJobPosted,
          min_applicants: minApplicants,
          min_job_posted: minJobPosted,
          name,
          plan_type: planType,
          start_date: startDate,
          status,
          // Not in the generated client until the spec is refreshed.
          ...(freeUnlockOfferId && freeUnlock
            ? {
                free_unlock_offer_id: freeUnlockOfferId,
                free_unlock: freeUnlock,
              }
            : {}),
        } as GetOrgCsvParam & {
          free_unlock_offer_id?: string;
          free_unlock?: string;
        },
      });

      const csvContent = response.data as unknown as string;

      const blob = new Blob([csvContent], {
        type: 'text/csv;charset=utf-8;',
      });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', 'google_metadata.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return csvContent;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getWhatsappTemplates = createAsyncThunk(
  'get/whatsappTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getNotificationsApi()
      ).notificationApiGetWhatsappTemplates();
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const sendWhatsappNotification = createAsyncThunk(
  'post/whatsapp',
  async (
    { data }: { data: WhatsappMarketingNotificationSuccessBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getNotificationsApi()
      ).notificationApiSendWhatsappMarketingNotification({
        whatsappMarketingNotificationSuccessBody: data,
      });
      if (data.dry_run !== true) {
        enqueueSnackbar('Notification Sent', { variant: 'success' });
      }

      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const sendMobileAppNotification = createAsyncThunk(
  'post/mobile-app-notification',
  async (
    { data }: { data: AdminSendMobileAppNotificationBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getNotificationsApi()
      ).notificationApiAdminSendMobileAppNotification({
        adminSendMobileAppNotificationBody: data,
      });
      if (data.dry_run !== true) {
        enqueueSnackbar('Mobile notification sent', { variant: 'success' });
      }
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export type TestCronNotificationMissingUser = {
  user_id: string;
  email: string | null;
  reason: string;
};

export type TestCronNotificationResponse = {
  success: boolean;
  notification_type: string;
  title: string;
  body: string;
  data: Record<string, unknown>;
  dry_run: boolean;
  eligible_user_count: number;
  sent_user_count: number;
  missing_token_user_count: number;
  unresolved_email_count: number;
  unresolved_user_id_count: number;
  missing_token_users: TestCronNotificationMissingUser[];
  unresolved_emails: string[];
  unresolved_user_ids: string[];
  notification_group_id: string | null;
};

export type TestCronNotificationBody = {
  notification_type: string;
  emails: string[];
  user_ids: string[];
  dry_run: boolean;
};

// TODO(spec-regen): switch to apiClient.getNotificationsApi() typed call after
// running `npm run update-spec` once the backend ships.
export const testCronMobileNotification = createAsyncThunk(
  'post/test-cron-mobile-notification',
  async ({ data }: { data: TestCronNotificationBody }, { rejectWithValue }) => {
    try {
      const response = await axios.post<TestCronNotificationResponse>(
        `${ConfigUrl.API_URL}/admin/test-cron-mobile-notification`,
        data,
        {
          headers: { Authorization: `Bearer ${apiClient.apiKey}` },
          withCredentials: true,
        },
      );
      if (data.dry_run === false) {
        enqueueSnackbar(`Sent to ${response.data.sent_user_count} user(s)`, {
          variant: 'success',
        });
      }
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response?.data);
    }
  },
);
export const getUsersList = createAsyncThunk(
  'get/user-list',
  async (
    {
      page,
      pageSize,
      userType,
      searchQuery,
      viewedOnboarding,
      filters,
      sortBy,
    }: {
      page: number;
      pageSize: number;
      userType?: string;
      searchQuery?: string;
      viewedOnboarding?: string[];
      filters?: UserMgmtFilter;
      sortBy?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGeneralApi()
      ).generalApiGetAllUserList({
        getUserListParams: {
          sort_by_latest_login: sortBy === 'last_login' ? 'desc' : undefined,
          sort_by_activity: sortBy === 'activity' ? 'desc' : undefined,
          page_no: page,
          page_size: pageSize,
          search_query: searchQuery,
          viewed_onboardings_list: viewedOnboarding,
          user_type: userType,
          max_experience: filters?.maxExperience,
          min_experience: filters?.minExperience,
          max_current_ctc: filters?.maxCTC,
          min_current_ctc: filters?.minCTC,
          phone_number: filters?.phoneNo,
          email: filters?.email,
          job_role_list: filters?.jobRole?.id
            ? [filters?.jobRole?.id]
            : undefined,
          competition_id: filters?.competitionId?.id,
          is_phone_number_exists: filters?.isPhone,
          only_interns: filters?.isIntern,
          skills_list: filters?.skills?.map((skill) => skill.id),
          min_credits: filters?.minCredits,
          max_credits: filters?.maxCredits,
          min_logins: filters?.minLogins,
          max_logins: filters?.maxLogins,
          min_activity_count: filters?.minActivity,
          max_activity_count: filters?.maxActivity,
          created_after: filters?.createdAfter,
          created_before: filters?.createdBefore,
          activity_type_list: filters?.activityTypes,
          max_last_job_posted: filters?.maxLastJobPosted,
          min_last_job_posted: filters?.minLastJobPosted,
          min_last_login: filters?.minLastLogin,
          max_last_login: filters?.maxLastLogin,
          is_premium: filters?.isPremium,
          signup_platform_type_list: filters?.signupPlatformTypes,
          app_type_list: filters?.appTypes,
          // has_app and has_chrome_extension are cast in until the generated
          // client is regenerated (npm run update-spec) after the backend
          // deploys.
          has_app: filters?.hasApp,
          has_chrome_extension: filters?.hasChromeExtension,
          country_list: filters?.countries,
          timezone_list: filters?.timezones,
          city: filters?.city,
        } as GetUserListParams & {
          has_app?: boolean;
          has_chrome_extension?: boolean;
        },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const getUserGeoOptionsAdmin = createAsyncThunk(
  'get/user-list-geo-options',
  async (_, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getGeneralApi()
      ).generalApiGetUserGeoOptions();
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const downloadCsvUsersAdmin = createAsyncThunk(
  'get/user-list-download-csv',
  async (
    {
      page,
      pageSize,
      userType,
      searchQuery,
      viewedOnboarding,
      filters,
      customUserId,
      sortBy,
    }: {
      page: number;
      pageSize: number;
      userType?: string;
      searchQuery?: string;
      viewedOnboarding?: string[];
      filters?: UserMgmtFilter;
      customUserId?: string[];
      sortBy?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGeneralApi()
      ).generalApiDownloadAllUserList({
        getUserListParams: {
          sort_by_latest_login: sortBy === 'last_login' ? 'desc' : undefined,
          sort_by_activity: sortBy === 'activity' ? 'desc' : undefined,
          page_no: page,
          page_size: pageSize,
          search_query: searchQuery,
          viewed_onboardings_list: viewedOnboarding,
          user_type: userType,
          max_experience: filters?.maxExperience,
          min_experience: filters?.minExperience,
          max_current_ctc: filters?.maxCTC,
          min_current_ctc: filters?.minCTC,
          phone_number: filters?.phoneNo,
          email: filters?.email,
          job_role_list: filters?.jobRole?.id
            ? [filters?.jobRole?.id]
            : undefined,
          competition_id: filters?.competitionId?.id,
          is_phone_number_exists: filters?.isPhone,
          only_interns: filters?.isIntern,
          skills_list: filters?.skills?.map((skill) => skill.id),
          min_credits: filters?.minCredits,
          max_credits: filters?.maxCredits,
          min_logins: filters?.minLogins,
          max_logins: filters?.maxLogins,
          min_activity_count: filters?.minActivity,
          max_activity_count: filters?.maxActivity,
          created_after: filters?.createdAfter,
          created_before: filters?.createdBefore,
          activity_type_list: filters?.activityTypes,
          min_last_job_posted: filters?.minLastJobPosted,
          max_last_job_posted: filters?.maxLastJobPosted,
          min_last_login: filters?.minLastLogin,
          max_last_login: filters?.maxLastLogin,
          custom_user_ids: customUserId,
          is_premium: filters?.isPremium,
          signup_platform_type_list: filters?.signupPlatformTypes,
          app_type_list: filters?.appTypes,
          // has_app and has_chrome_extension are cast in until the generated
          // client is regenerated (npm run update-spec) after the backend
          // deploys.
          has_app: filters?.hasApp,
          has_chrome_extension: filters?.hasChromeExtension,
          country_list: filters?.countries,
          timezone_list: filters?.timezones,
          city: filters?.city,
        } as GetUserListParams & {
          has_app?: boolean;
          has_chrome_extension?: boolean;
        },
      });
      const csvContent = response.data as unknown as string;
      const blob = new Blob([csvContent], {
        type: 'text/csv;charset=utf-8;',
      });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', 'Users.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return csvContent;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const adminUserUpdate = createAsyncThunk(
  'put/user-data',
  async (
    {
      userId,
      updateBody,
    }: {
      userId: string;
      updateBody: AdminUserUpdateBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiAdminUserUpdate({ userId, adminUserUpdateBody: updateBody });
      enqueueSnackbar('User Updated', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const adminUserDelete = createAsyncThunk(
  'delete/user-data',
  async (
    {
      userId,
    }: {
      userId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiDeleteUserCompletely({ userId });
      enqueueSnackbar('User Deleted', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const adminUserReset = createAsyncThunk(
  'reset/user-data',
  async (
    {
      userId,
    }: {
      userId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiResetUserCompletely({ userId });
      enqueueSnackbar('User Reset done', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const addMatchingJobToUser = createAsyncThunk(
  'add/job-to-user',
  async (
    {
      jobId,
      body,
    }: {
      jobId: string;
      body: JobProfileMatchList;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiAddJobProfile({ jobId, jobProfileMatchList: body });

      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAllAdminChatTemplates = createAsyncThunk(
  'get/admin-chat-templates',
  async (
    {
      page,
      pageSize,
      query,
    }: { page?: number; pageSize?: number; query?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getOrgApi()
      ).organizationApiAdminGetChatTemplateList({ page, pageSize, query });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const createAdminChatTemplate = createAsyncThunk(
  'post/admin-chat-template',
  async (
    adminCreateChatTemplateBody: AdminCreateChatTemplateBody,
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getOrgApi()
      ).organizationApiAdminCreateChatTemplate({
        adminCreateChatTemplateBody,
      });
      enqueueSnackbar('Template Created Successfully', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateAdminChatTemplate = createAsyncThunk(
  'put/admin-chat-template',
  async (
    {
      templateId,
      data,
    }: {
      templateId: string;
      data: AdminCreateChatTemplateBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getOrgApi()
      ).organizationApiAdminUpdateChatTemplateById({
        chatTemplateId: templateId,
        adminCreateChatTemplateBody: data,
      });
      enqueueSnackbar('Template Updated Successfully', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteAdminChatTemplate = createAsyncThunk(
  'delete/admin-chat-template',
  async ({ templateId }: { templateId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getOrgApi()
      ).organizationApiAdminDeleteChatTemplateById({
        chatTemplateId: templateId,
      });
      enqueueSnackbar('Template Deleted Successfully', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const sendBulkTemplateMessageToEmployer = createAsyncThunk(
  'post/bulk-template-message-to-employer',
  async (
    bulkTemplateSupportBody: BulkTemplateSupportBody,
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiSendBulkTemplateMessagesByAdmin({ bulkTemplateSupportBody });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
let mailGroupsLogsAbortController: AbortController | null = null;
export const getMailGroupsLogs = createAsyncThunk(
  'get/email-group-logs',
  async (
    {
      body,
    }: {
      body: GetEmailGroupLogsListParam;
    },
    { rejectWithValue },
  ) => {
    try {
      if (mailGroupsLogsAbortController) {
        mailGroupsLogsAbortController.abort();
      }
      mailGroupsLogsAbortController = new AbortController();
      const response = await (
        await apiClient.getMailAPi()
      ).mailApiGetEmailGroupLogsList(
        { getEmailGroupLogsListParam: body },
        { signal: mailGroupsLogsAbortController.signal },
      );
      return response.data;
    } catch (err: any) {
      if (!axios.isCancel(err)) {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response?.data);
    }
  },
);
// Resolves the one group a shared ?group_id=<id> link points at. It deliberately
// bypasses the list state so opening a shared link never rewrites the log list
// the receiver is looking at.
export const getMailGroupById = createAsyncThunk(
  'get/email-group-by-id',
  async ({ emailGroupId }: { emailGroupId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getMailAPi()
      ).mailApiGetEmailGroupLogsList({
        getEmailGroupLogsListParam: {
          page_no: 1,
          page_size: 1,
          // email_group_id lands in the generated client on the next update-spec.
          email_group_id: emailGroupId,
        } as GetEmailGroupLogsListParam & { email_group_id: string },
      });
      return response.data?.email_group?.[0] ?? null;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response?.data);
    }
  },
);
export const getMailGroupUsersByID = createAsyncThunk(
  'get/email-log-by-id',
  async (
    {
      emailGroupId,
      page,
      pageSize,
      sortBy,
      sortOrder,
    }: {
      emailGroupId: string;
      page: number;
      pageSize: number;
      sortBy?: string;
      sortOrder: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getMailAPi()
      ).mailApiGetEmailGroupUsersList({
        emailGroupId,
        pageNo: page,
        pageSize,
        sortBy,
        sortOrder,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const getEmailGroupSegmentCounts = createAsyncThunk(
  'get/email-group-segment-counts',
  async (emailGroupId: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getMailAPi()
      ).mailApiGetEmailGroupSegmentCounts({ emailGroupId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  },
);
export const getEmailGroupThread = createAsyncThunk(
  'get/email-group-thread',
  async (emailGroupId: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getMailAPi()
      ).mailApiGetEmailGroupThread({ emailGroupId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  },
);
export const sendFollowUpEmail = createAsyncThunk(
  'post/email-group-follow-up',
  async (
    {
      emailGroupId,
      body,
    }: {
      emailGroupId: string;
      body: SendFollowUpBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getMailAPi()
      ).mailApiSendFollowUpEmail({ emailGroupId, sendFollowUpBody: body });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response?.data);
    }
  },
);
export const getMocktestAdmin = createAsyncThunk(
  'get/mocktest-admin',
  async (
    {
      searchQuery,
      pageNo,
      pageSize,
      skillIds,
    }: {
      searchQuery?: string;
      pageNo?: number;
      pageSize?: number;
      skillIds?: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getMockTestApi()
      ).mocktestApiGetAdminMockExams({
        name: searchQuery,
        pageNo,
        pageSize,
        skillIds,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const uploadMocktestImage = createAsyncThunk(
  'post/upload-mocktest-image',
  async ({ file }: { file: any }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getExamApi()
      ).examApiUploadExamImage({ imageFile: file });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const createMocktest = createAsyncThunk(
  'post/mocktest',
  async (
    { mocktest }: { mocktest: CreateAdminMockTestBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getMockTestApi()
      ).mocktestApiCreateMocktestAdmin({ createAdminMockTestBody: mocktest });
      enqueueSnackbar('Mocktest Created Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const generateMocktest = createAsyncThunk(
  'put/generate-skilltest',
  async ({ body }: { body: GenerateSkillTestBody }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getMockTestApi()
      ).mocktestApiGenerateSkilltests({ generateSkillTestBody: body });
      enqueueSnackbar('Mocktest Generated Successfully', {
        variant: 'success',
      });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const updateMocktestAdmin = createAsyncThunk(
  'put/mocktest-admin',
  async (
    {
      mocktestId,
      mocktest,
    }: { mocktestId: string; mocktest: UpdateMockTestBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getMockTestApi()
      ).mocktestApiUpdateMocktestDetail({
        mocktestId,
        updateMockTestBody: mocktest,
      });
      enqueueSnackbar('Mocktest Updated Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const deleteMocktestAdmin = createAsyncThunk(
  'delete/mocktest-admin',
  async ({ mocktestId }: { mocktestId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getMockTestApi()
      ).mocktestApiDeleteExam({ mocktestId });
      enqueueSnackbar('Mocktest Deleted Successfully', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getMocktestById = createAsyncThunk(
  'get/mocktest-by-id',
  async ({ mocktestId }: { mocktestId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getMockTestApi()
      ).mocktestApiGetMocktestById({ mocktestId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

let reviewJobsAbortController: AbortController | null = null;

export const getReviewQuestions = createAsyncThunk(
  'get/review-questions',
  async ({ body }: { body: GetUnderReviewParams }, { rejectWithValue }) => {
    try {
      if (reviewJobsAbortController) {
        reviewJobsAbortController.abort();
      }
      reviewJobsAbortController = new AbortController();

      const response = await (
        await apiClient.getReviewApi()
      ).reviewApiGetQuestionUnderreviewList(
        { getUnderReviewParams: body },
        { signal: reviewJobsAbortController.signal },
      );
      reviewJobsAbortController = null;

      return response.data;
    } catch (err: any) {
      reviewJobsAbortController = null;
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') {
        return rejectWithValue({ cancelled: true });
      }

      return rejectWithValue(err?.response?.data ?? err);
    }
  },
);

export const getQuestionReviewTemplates = createAsyncThunk(
  'get/question-review-templates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getReviewApi()
      ).reviewApiReviewTemplateApi();
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const createQuestionReviewTemplates = createAsyncThunk(
  'post/question-review-templates',
  async (
    { name, description }: { name: string; description: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getReviewApi()
      ).reviewApiCreateReviewTemplateApi({
        createReviewTemplateBody: { name, description },
      });
      enqueueSnackbar('Template Created Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const deleteQuestionReviewTemplates = createAsyncThunk(
  'delete/question-review-templates',
  async ({ templateId }: { templateId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getReviewApi()
      ).reviewApiDeleteReviewTemplateApi({ reviewTemplateId: templateId });
      enqueueSnackbar('Template Deleted Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const questionBulkStatusUpdate = createAsyncThunk(
  'put/question-bulk-update',
  async (
    {
      questionIds,
      status,
      rejectReason,
    }: { questionIds: string[]; status: string; rejectReason?: string },
    { rejectWithValue },
  ) => {
    const body = questionIds.map((id) => ({
      question_status: status,
      question_id: id,
      ...(rejectReason ? { rejection_reason: rejectReason } : {}),
    }));

    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiUpdateUnderReviewDetailsBulk({
        bulkUpdateUnderReviewStatusParam: { body },
      });
      enqueueSnackbar('Status Updated Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const activateAllAIQuestionsByModule = createAsyncThunk(
  'admin/activateAllAIQuestionsByModule',
  async (
    {
      action,
      questionStatus,
      userId,
      minDate,
      maxDate,
      questionType,
      withHint,
      withExam,
      questionId,
      searchQuery,
      skillIds,
      isAIGenerated,
      dryRun,
    }: {
      action: 'ACTIVATE' | 'DELETE' | 'UNMAP';
      questionStatus?: string;
      userId?: string;
      minDate?: string;
      maxDate?: string;
      questionType?: string;
      withHint?: boolean;
      withExam?: boolean;
      questionId?: string;
      searchQuery?: string;
      skillIds?: string[];
      isAIGenerated?: boolean;
      dryRun?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiBulkActionsReviewQuestions({
        bulkActionsReviewQuestionBody: {
          action,
          question_status: questionStatus,
          user_id: userId,
          min_date: minDate,
          max_date: maxDate,
          question_type: questionType,
          with_hint: withHint !== undefined ? String(withHint) : undefined,
          with_exam: withExam !== undefined ? String(withExam) : undefined,
          question_id: questionId,
          search_query: searchQuery,
          skill_ids: skillIds,
          is_ai_generated: isAIGenerated,
          dry_run: dryRun,
        },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getQuestionAuthors = createAsyncThunk(
  'get/question-authors',
  async ({ searchQuery }: { searchQuery: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getGeneralApi()
      ).generalApiQuestionAuthorAutocomplete({
        page: '1',
        pageSize: '10',
        query: searchQuery,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const uploadEmailImage = createAsyncThunk(
  'post/upload-email-image',
  async ({ file }: { file: any }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getFilesApi()
      ).filesApiUploadMailBodyImg({ file });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAllLevels = createAsyncThunk(
  'get/levels',
  async (_, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getLevelApi()
      ).levelApiGetAllLevels();
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getLevelById = createAsyncThunk(
  'get/level-by-id',
  async ({ levelId }: { levelId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getLevelApi()
      ).levelApiGetLevelById({ levelId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const createLevel = createAsyncThunk(
  'post/create-level',
  async (
    {
      body,
    }: {
      body: CreateLevelBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getLevelApi()
      ).levelApiCreateLevel({ createLevelBody: body });
      enqueueSnackbar('Level Created Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const updateLevel = createAsyncThunk(
  'post/update-level',
  async (
    {
      levelId,
      body,
    }: {
      levelId: string;
      body: UpdateLevelBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getLevelApi()
      ).levelApiUpdateLevel({ levelId, updateLevelBody: body });
      enqueueSnackbar('Level Updated Successfully', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const deleteLevelById = createAsyncThunk(
  'delete/level',
  async ({ levelId }: { levelId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getLevelApi()
      ).levelApiDeleteLevel({ levelId });
      enqueueSnackbar('Level Deleted Successfully', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const uploadLevelImage = createAsyncThunk(
  'post/upload-level-image',
  async ({ file }: { file: any }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getLevelApi()
      ).levelApiUploadLevelImages({ image: file });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAllBadge = createAsyncThunk(
  'get/badge',
  async (
    {
      page,
      pageSize,
      searchQuery,
    }: { page: number; pageSize: number; searchQuery?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getBadgeApi()
      ).badgeApiGetBadge({ page, pageSize, query: searchQuery });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getBadgeType = createAsyncThunk(
  'get/badge-types',
  async (_, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getBadgeApi()
      ).badgeApiGetBadgeType();
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const createBadge = createAsyncThunk(
  'post/create-badge',
  async (
    {
      body,
    }: {
      body: CreateBadgeBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getBadgeApi()
      ).badgeApiCreateNewBadge({ createBadgeBody: body });
      enqueueSnackbar('Badge Created Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const updateBadge = createAsyncThunk(
  'put/update-badge',
  async (
    {
      body,
    }: {
      body: GetAllBadgesSub;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getBadgeApi()
      ).badgeApiUpdateBadge({ getAllBadgesSub: body });
      enqueueSnackbar('Badge Updated Successfully', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const deleteBadgeById = createAsyncThunk(
  'delete/badge',
  async ({ badgeId }: { badgeId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getBadgeApi()
      ).badgeApiDeleteBadge({ badgeId });
      enqueueSnackbar('Badge Deleted Successfully', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const uploadBadgeImage = createAsyncThunk(
  'post/upload-badge-image',
  async ({ file }: { file: any }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getBadgeApi()
      ).badgeApiUploadBadgeImage({ imageFile: file });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAdminFeedbacks = createAsyncThunk(
  'get/admin-feedbacks',
  async (
    {
      areaOfIssue,
      dateMax,
      dateMin,
      feedbackType,
      issueType,
      page,
      pageSize,
      searchQuery,
      sortBy,
      sortOrder,
      status,
    }: {
      areaOfIssue?: string;
      dateMax?: string;
      dateMin?: string;
      feedbackType?: string;
      issueType?: string;
      searchQuery?: string;
      page?: number;
      pageSize?: number;
      sortBy?: string;
      sortOrder?: string;
      status?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGeneralApi()
      ).generalApiGetFeedbackReports({
        areaOfIssue,
        dateMax,
        dateMin,
        feedbackType,
        issueType,
        name: searchQuery,
        pageNo: page,
        pageSize,
        sortBy,
        sortOrder,
        status,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const updateAdminFeedbackStatus = createAsyncThunk(
  'get/admin-update-feedback-status',
  async (
    {
      feedbackId,
      status,
    }: {
      feedbackId?: string;
      status?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGeneralApi()
      ).generalApiUpdateUserFeedbackStatus({ feedbackId, status });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAdminFeedbackThread = createAsyncThunk(
  'get/admin-feedback-thread',
  async ({ feedbackId }: { feedbackId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getGeneralApi()
      ).generalApiGetAdminFeedbackThread({ feedbackId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const adminReplyToFeedback = createAsyncThunk(
  'post/admin-reply-to-feedback',
  async (
    {
      feedbackId,
      body,
      mediaUrlList,
      status,
    }: {
      feedbackId: string;
      body: string;
      mediaUrlList?: string[];
      status?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGeneralApi()
      ).generalApiAdminReplyToFeedback({
        feedbackId,
        feedbackReplyBody: {
          body,
          media_url_list: mediaUrlList,
          status,
        },
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const allocateCreditsAdmin = createAsyncThunk(
  'put/admin-allocate-credits',
  async (
    {
      userId,
      creditCount,
    }: {
      userId?: string;
      creditCount?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getCreditsApi()
      ).creditsApiAdminAllocateCreditToUsers({
        creditsCount: creditCount,
        userId,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

// Employer feedback is rewarded to the ORG credit wallet (not a candidate's
// personal credits), so it goes through its own endpoint — never
// allocateCreditsAdmin, which credits Users.credits.
export const rewardEmployerFeedback = createAsyncThunk(
  'put/admin-reward-employer-feedback',
  async (
    { feedbackId, amount }: { feedbackId: string; amount: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGeneralApi()
      ).generalApiRewardEmployerFeedback({
        feedbackId,
        rewardEmployerFeedbackBody: { amount },
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const rewardBonusAdmin = createAsyncThunk(
  'post/reward-bonus-admin',
  async (
    { userId, credits }: { userId: string; credits: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiRewardBonusToUser({
        rewardBonusBody: { credits, user_ids: [userId] },
      });
      enqueueSnackbar('Bonus Credits Rewarded', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const downloadOrgCSV = createAsyncThunk(
  'put/admin-allocate-credits',
  async (
    {
      endDate,
      startDate,
      name,
      isDocument,
      status,
      maxJobPosted,
      minJobPosted,
      minApplicants,
      planType,
      freeUnlockOfferId,
      freeUnlock,
    }: {
      endDate?: string;
      startDate?: string;
      name?: string;
      isDocument?: boolean;
      status?: string;
      maxJobPosted?: number;
      minJobPosted?: number;
      minApplicants?: number;
      planType?: string;
      // The Review Orgs offer filter; only sent as a pair.
      freeUnlockOfferId?: string;
      freeUnlock?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getOrgApi()
      ).organizationApiGetOrganizationCsv(
        {
          document: isDocument,
          endDate,
          maxJobPosted,
          minApplicants,
          minJobPosted,
          name,
          planType,
          startDate,
          status,
        },
        // Not in the generated client until the spec is refreshed; axios appends
        // `params` to the query string the client built.
        {
          params:
            freeUnlockOfferId && freeUnlock
              ? {
                  free_unlock_offer_id: freeUnlockOfferId,
                  free_unlock: freeUnlock,
                }
              : undefined,
        },
      );
      const csvContent = response.data as unknown as string;

      const blob = new Blob([csvContent], {
        type: 'text/csv;charset=utf-8;',
      });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', 'org_data.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return csvContent;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getStoreItemsAdmin = createAsyncThunk(
  'get/admin-store-items',
  async (
    {
      isActive,
      itemId,
      searchQuery,
      availableTo,
      itemType,
      isReward,
      isOfferItem,
    }: {
      isActive?: boolean;
      itemId?: string;
      searchQuery?: string;
      availableTo?: PaymentApiGetAllItemsAdminAvailableToEnum;
      itemType?: StoreItemType;
      isReward?: boolean;
      isOfferItem?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiGetAllItemsAdmin({
        itemId,
        userSearchQuery: searchQuery,
        webIsActive: isActive,
        availableTo,
        // The generated enum lacks the Pro tiers until the spec is regenerated
        // (see StoreItemType); the backend accepts them.
        itemType: itemType as PaymentApiGetAllItemsAdminItemTypeEnum,
        isReward,
        isOfferItem,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getStoreItemTypeCounts = createAsyncThunk(
  'get/admin-store-item-type-counts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiGetStoreItemTypeCounts();
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const createStoreItemsAdmin = createAsyncThunk(
  'post/admin-store-items',
  async (
    {
      body,
    }: {
      body: AddNewStoreItemBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiAddNewItem({ addNewStoreItemBody: body });
      enqueueSnackbar('Product Created Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const updateStoreItemsAdmin = createAsyncThunk(
  'put/admin-store-items',
  async (
    {
      itemId,
      body,
    }: {
      itemId: string;
      body: AddNewStoreItemBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiUpdatePurchasableItems({
        itemId,
        addNewStoreItemBody: body,
      });
      enqueueSnackbar('Product Updated Successfully', {
        variant: 'success',
      });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const deleteStoreItemsAdmin = createAsyncThunk(
  'delete/admin-store-items',
  async (
    {
      itemId,
    }: {
      itemId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiDeleteItem({ itemId });
      enqueueSnackbar('Product Deleted Successfully', {
        variant: 'success',
      });

      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response.data);
    }
  },
);
export const uploadStoreItemImage = createAsyncThunk(
  'post/upload-store-item-image',
  async ({ file }: { file: any }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiUploadItemImage({ imageFile: file });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const syncStoreItemAdmin = createAsyncThunk(
  'post/sync-store-item',
  async (
    { itemId, force }: { itemId: string; force?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiSyncStoreItem({ itemId, syncStoreItemBody: { force } });
      enqueueSnackbar('Sync started', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getStoreItemSyncStatusAdmin = createAsyncThunk(
  'get/store-item-sync-status',
  async ({ itemId }: { itemId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiGetStoreItemSyncStatus({ itemId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const refreshAppleStoreStatusAdmin = createAsyncThunk(
  'post/refresh-apple-store-status',
  async ({ itemId }: { itemId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiRefreshStoreItemAppleStatus({ itemId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response.data);
    }
  },
);

export const uploadAppleReviewScreenshotAdmin = createAsyncThunk(
  'post/upload-apple-review-screenshot',
  async (
    { itemId, imageFile }: { itemId: string; imageFile?: File },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiUploadStoreItemAppleReviewScreenshot({ itemId, imageFile });
      // Same 200-with-last_sync_error contract as
      // submitStoreItemAppleReviewAdmin below — upload_apple_review_screenshot_ctrl
      // reports Apple's refusal in the body, not the status code.
      if (response.data?.last_sync_error) {
        enqueueSnackbar(response.data.last_sync_error, { variant: 'error' });
      } else {
        enqueueSnackbar('Review screenshot uploaded', { variant: 'success' });
      }
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response.data);
    }
  },
);

export const submitStoreItemAppleReviewAdmin = createAsyncThunk(
  'post/submit-store-item-apple-review',
  async ({ itemId }: { itemId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiSubmitStoreItemAppleReview({ itemId });
      // The endpoint answers 200 with last_sync_error set when it declined to
      // submit (e.g. Apple would not accept the edited name/description, so a
      // review would have covered the old copy) — reporting success there
      // would tell the admin the opposite of what happened.
      if (response.data?.last_sync_error) {
        enqueueSnackbar(response.data.last_sync_error, { variant: 'error' });
      } else {
        enqueueSnackbar('Submitted for Apple review', { variant: 'success' });
      }
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response.data);
    }
  },
);

export const adminSupportRedeem = createAsyncThunk(
  'post/admin-support-redeem',
  async (
    {
      email,
      gemsAmount,
      transactionDate,
    }: { email: string; gemsAmount: number; transactionDate?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiAdminSupportRedeem({
        adminSupportRedeemBody: {
          email,
          gems_amount: gemsAmount,
          transaction_date: transactionDate ?? null,
        },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);
export const getOrdersAdmin = createAsyncThunk(
  'get/order-admin',
  async (
    {
      page,
      pageSize,
      startDate,
      endDate,
      status,
    }: {
      page?: number;
      pageSize?: number;
      startDate?: string;
      endDate?: string;
      status?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiGetAllOrders({
        endDate,
        pageNo: page,
        pageSize,
        paymentStatus: status,
        startDate,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getOrdersUserAddress = createAsyncThunk(
  'get/order-adress',
  async (
    {
      userId,
    }: {
      userId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiAdminGetUserShippingDetails({ userId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getS3UrlOfFile = createAsyncThunk(
  'get/s3-url',
  async (
    {
      fileUrl,
    }: {
      fileUrl: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getFilesApi()
      ).filesApiDownloadFile({ fileUrl });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAllPlansAdmin = createAsyncThunk(
  'get/all-plans-admin',
  async (
    {
      page,
      pageSize,
      status,
    }: {
      page?: number;
      pageSize?: number;
      status?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getOrgApi()
      ).organizationApiGetPlanListPublic({ page, pageSize, status });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const editOrderAdmin = createAsyncThunk(
  'put/order-admin',
  async (
    {
      transactionId,
      body,
    }: {
      transactionId: string;
      body: AdminUpdateTrasnsactionBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiUpdateTransactionDetails({
        transactionId,
        adminUpdateTrasnsactionBody: body,
      });
      enqueueSnackbar('Updated Successfully', {
        variant: 'success',
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const createPotentialRecruitersBulk = createAsyncThunk(
  'post/potential-recruiters-bulk',
  async (
    { body }: { body: CreatePotentialRecruiterBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiCreateBulkPotentialRecruiters({
        createPotentialRecruiterBody: body,
      });
      enqueueSnackbar('Data Added Successfully', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getPotentialRecruitersBulk = createAsyncThunk(
  'get/potential-recruiters-bulk',
  async (
    { body }: { body: PotentialRecruiterParameter },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiGetPotentialRecruiters({ potentialRecruiterParameter: body });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getCollegeData = createAsyncThunk(
  'get/college-data',
  async (
    {
      collegeName,
      pageNo,
      pageSize,
      status,
      sortBy,
      sortOrder,
      skipStore,
    }: {
      collegeName?: string;
      pageNo?: number;
      pageSize?: number;
      status?: string;
      sortBy?: string;
      sortOrder?: string;
      skipStore?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiGetCollege({
        collegeName,
        page: pageNo,
        pageSize,
        sortBy,
        sortOrder,
        status,
      });
      return {
        data: response.data,
        skipStore,
      };
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getCompanyData = createAsyncThunk(
  'get/company-data',
  async (
    {
      companyName,
      pageNo,
      pageSize,
      status,
      sortBy,
      sortOrder,
      skipStore,
    }: {
      companyName?: string;
      pageNo?: number;
      pageSize?: number;
      status?: string;
      sortBy?: string;
      sortOrder?: string;
      skipStore?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiGetCompany({
        companyName,
        page: pageNo,
        pageSize,
        sortBy,
        sortOrder,
        status,
      });
      return {
        data: response.data,
        skipStore,
      };
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getExamData = createAsyncThunk(
  'get/exam-data',
  async (
    {
      searchQuery,
      pageNo,
      pageSize,
      status,
      sortBy,
      sortOrder,
      skipStore,
    }: {
      searchQuery?: string;
      pageNo?: number;
      pageSize?: number;
      status?: string;
      sortBy?: string;
      sortOrder?: string;
      skipStore?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getExamApi()
      ).examApiGetExamDetail({
        pageNo,
        pageSize,
        sortBy,
        sortOrder,
        status,
        userSearchQuery: searchQuery,
      });
      return {
        data: response.data,
        skipStore,
      };
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getJobRoleData = createAsyncThunk(
  'get/jobrole-data',
  async (
    {
      searchQuery,
      pageNo,
      pageSize,
      status,
      sortBy,
      sortOrder,
      showJobAssociated,
      skipStore,
    }: {
      searchQuery?: string;
      pageNo?: number;
      pageSize?: number;
      status?: string;
      sortBy?: string;
      sortOrder?: string;
      showJobAssociated: boolean;
      skipStore?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiGetJobRole({
        pageNo: String(pageNo),
        pageSize: String(pageSize),
        showJobAssociatedRoles: showJobAssociated,
        sortBy,
        sortOrder,
        status,
        userSearchQuery: searchQuery,
      });
      return {
        data: response.data,
        skipStore,
      };
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getSkillsData = createAsyncThunk(
  'get/skill-data',
  async (
    {
      searchQuery,
      pageNo,
      pageSize,
      status,
      sortBy,
      sortOrder,
      showJobAssociated,
      skipStore,
    }: {
      searchQuery?: string;
      pageNo?: number;
      pageSize?: number;
      status?: string;
      sortBy?: string;
      sortOrder?: string;
      showJobAssociated: boolean;
      skipStore?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiAdminGetJobSkills({
        name: searchQuery,
        pageNo,
        pageSize,
        showJobAssociatedSkills: showJobAssociated,
        sortBy,
        sortOrder,
        status,
      });
      return {
        data: response.data,
        skipStore,
      };
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteCollege = createAsyncThunk(
  'delelte/college-data',
  async (
    {
      ids,
    }: {
      ids: Array<string>;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiDeleteCollege({ iDList: { ids } });
      enqueueSnackbar('College Deleted Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const deleteCompany = createAsyncThunk(
  'delelte/company-data',
  async (
    {
      ids,
    }: {
      ids: Array<string>;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiDeleteCompany({ iDList: { ids } });
      enqueueSnackbar('Company Deleted Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const createCollege = createAsyncThunk(
  'post/college-data',
  async (
    {
      body,
    }: {
      body: CreateCollegeBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiCollegeDetails({ createCollegeBody: body });
      enqueueSnackbar('College Added Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const updateCollege = createAsyncThunk(
  'put/college-data',
  async (
    {
      collegeId,
      body,
    }: {
      collegeId: string;
      body: CreateCollegeBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiUpdateCollege({
        collegeId,
        address: body.address,
        approvalStatus: body.approval_status,
        collegeAdminId: body.college_admin_id || undefined,
        collegeName: body.college_name,
        collegeType: body.college_type,
        defaultColor: body.default_color || undefined,
        description: body.description,
        directorName: body.director_name,
        districtName: body.district_name,
        email: body.email,
        imageUrl: body.image_url || undefined,
        jointDirectorName: body.joint_director_name,
        phoneNumber: body.phone_number,
        stateName: body.state_name,
        universityName: body.university_name,
      });
      enqueueSnackbar('College Updated Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const createCompany = createAsyncThunk(
  'post/company-data',
  async (
    {
      body,
    }: {
      body: CreateCompanyBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiCompanyDetails({ createCompanyBody: body });
      enqueueSnackbar('Company Added Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const updateCompany = createAsyncThunk(
  'put/company-data',
  async (
    {
      companyId,
      body,
    }: {
      companyId: string;
      body: CreateCompanyBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiUpdateCompany({
        companyId,
        approvalStatus: body.approval_status || undefined,
        companyDescription: body.company_description || undefined,
        companyName: body.company_name,
        companySize: body.company_size || undefined,
        corporateIdentificationNumber:
          body.corporate_identification_number || undefined,
        defaultColor: body.default_color || undefined,
        imageUrl: body.image_url || undefined,
      });
      enqueueSnackbar('Company Updated Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const createExam = createAsyncThunk(
  'post/exam-data',
  async (
    {
      body,
    }: {
      body: CreateExamBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getExamApi()
      ).examApiCreateExamDetail({ createExamBody: body });
      enqueueSnackbar('Exam Added Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const updateExam = createAsyncThunk(
  'put/exam-data',
  async (
    {
      examId,
      body,
    }: {
      examId: string;
      body: CreateExamBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getExamApi()
      ).examApiUpdateExamDetail({ examId, createExamBody: body });
      enqueueSnackbar('Exam Updated Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const deleteExam = createAsyncThunk(
  'delelte/exam-data',
  async (
    {
      ids,
    }: {
      ids: Array<string>;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getExamApi()
      ).examApiDeleteExamDetail({ iDList: { ids } });
      enqueueSnackbar('Exam Deleted Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const createJobRole = createAsyncThunk(
  'post/jobRole-data',
  async (
    {
      body,
    }: {
      body: CreateJobRoleBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiCreateJobRole({ createJobRoleBody: body });
      enqueueSnackbar('Job Role Added Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const updateJobRoleData = createAsyncThunk(
  'put/jobrole-data',
  async (
    {
      body,
    }: {
      body: Array<UpdateJobRoleBody>;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiUpdateJobRole({ updateJobRoleBody: body });
      enqueueSnackbar('Job Role Updated Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const deleteJobRole = createAsyncThunk(
  'delelte/jobrole-data',
  async (
    {
      ids,
    }: {
      ids: Array<string>;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiDeleteJobRole({ iDList: { ids } });
      enqueueSnackbar('Job Role Deleted Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getDegreeData = createAsyncThunk(
  'get/degree-data',
  async (
    {
      searchQuery,
      pageNo,
      pageSize,
      status,
      sortBy,
      sortOrder,
      skipStore,
    }: {
      searchQuery?: string;
      pageNo?: number;
      pageSize?: number;
      status?: string;
      sortBy?: string;
      sortOrder?: string;
      skipStore?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetDegree({
        approvalStatus: status,
        pageNo: String(pageNo),
        pageSize: String(pageSize),
        sortBy,
        sortOrder,
        userSearchQuery: searchQuery,
      });
      return {
        data: response.data,
        skipStore,
      };
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const createDegree = createAsyncThunk(
  'post/degree-data',
  async (
    {
      body,
    }: {
      body: CreateDegreeBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiCreateDegree({ createDegreeBody: [body] });
      enqueueSnackbar('Degree Added Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const updateDegree = createAsyncThunk(
  'put/jobrole-data',
  async (
    {
      body,
    }: {
      body: UpdateDegreeBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiUpdateDegree({ updateDegreeBody: body });
      enqueueSnackbar('Degree Updated Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const deleteDegree = createAsyncThunk(
  'delelte/degree-data',
  async (
    {
      id,
    }: {
      id: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiDeleteDegree({ degreeId: id });
      enqueueSnackbar('Degree Deleted Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getSpecializationData = createAsyncThunk(
  'get/specialization-data',
  async (
    {
      searchQuery,
      pageNo,
      pageSize,
      status,
      sortBy,
      sortOrder,
      degreeId,
      skipStore,
    }: {
      searchQuery?: string;
      pageNo?: number;
      pageSize?: number;
      status?: string;
      sortBy?: string;
      sortOrder?: string;
      degreeId?: string;
      skipStore?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetSpecialization({
        approvalStatus: status,
        degreeId,
        pageNo: String(pageNo),
        pageSize: String(pageSize),
        sortBy,
        sortOrder,
        userSearchQuery: searchQuery,
      });
      return {
        data: response.data,
        skipStore,
      };
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const createSpecialization = createAsyncThunk(
  'post/specialization-data',
  async (
    {
      body,
    }: {
      body: CreateSpecializationBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiCreateSpecialization({ createSpecializationBody: [body] });
      enqueueSnackbar('Specialization Added Successfully', {
        variant: 'success',
      });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const updateSpecialization = createAsyncThunk(
  'put/specialization-data',
  async (
    {
      body,
    }: {
      body: UpdateSpecializationBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiUpdateSpecialization({ updateSpecializationBody: body });
      enqueueSnackbar('Specialization Updated Successfully', {
        variant: 'success',
      });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const deleteSpecialization = createAsyncThunk(
  'delelte/speciailzation-data',
  async (
    {
      id,
    }: {
      id: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiDeleteSpecialization({ specializationId: id });
      enqueueSnackbar('Specialization Deleted Successfully', {
        variant: 'success',
      });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const createJobSkill = createAsyncThunk(
  'post/jobRole-data',
  async (
    {
      body,
    }: {
      body: CreateSkillBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiCreateSkillBulk({ createSkillBody: body });
      enqueueSnackbar('Skill Added Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const updateJobSkill = createAsyncThunk(
  'put/jobrole-data',
  async (
    {
      skillId,
      body,
    }: {
      skillId: string;
      body: UpdateSkillBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiUpdateSkill({ skillId, updateSkillBody: body });
      enqueueSnackbar('Skill Updated Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const deleteJobSilll = createAsyncThunk(
  'delelte/jobrole-data',
  async (
    {
      ids,
    }: {
      ids: Array<string>;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiDeleteSkill({ iDList: { ids } });
      enqueueSnackbar('Skill Deleted Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const uploadExamImage = createAsyncThunk(
  'post/exam-img',
  async (
    {
      file,
    }: {
      file: any;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getExamApi()
      ).examApiUploadExamImage({ imageFile: file });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const uploadMiscImg = createAsyncThunk(
  'post/upload-misc-img',
  async (
    {
      file,
    }: {
      file: any;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiUploadMiscImage({ imageFile: file });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const mergeEntities = createAsyncThunk(
  'put/merge-entities',
  async (
    {
      mainEntityId,
      entityType,
      duplicateIds,
    }: {
      mainEntityId: string;
      entityType: string;
      duplicateIds: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGeneralApi()
      ).generalApiMergeSimilarOrDuplicateEnitites({
        mergeDuplicateEntityBody: { duplicate_ids: duplicateIds },
        entityType,
        mainEntityId,
      });
      enqueueSnackbar('Merged Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const splitEntities = createAsyncThunk(
  'put/merge-entities',
  async (
    {
      mainEntityId,
      entityType,
      newEntities,
    }: {
      mainEntityId: string;
      entityType: string;
      newEntities: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGeneralApi()
      ).generalApiSplitEntities({
        splitEntityBody: { new_entity_names: newEntities },
        entityType,
        mainEntityId,
      });
      enqueueSnackbar('Split Successful', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAdminResumeXrayStatistics = createAsyncThunk(
  'get/xray-resume-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetJobApplicantsSummary({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getAdminSignupSourceStatistics = createAsyncThunk(
  'get/signup-source-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetUserSignupsByUtmSource({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAdminSignupPageStatistics = createAsyncThunk(
  'get/signup-page-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetUserSignupsByPageUrl({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAdminEmailStatistics = createAsyncThunk(
  'get/email-stats',
  async (
    {
      startDate,
      endDate,
      granularity,
      userType,
      platform,
    }: {
      startDate: string | undefined;
      endDate: string | undefined;
      granularity: string;
      userType: string | undefined;
      platform?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getStatsApi()
      ).statisticsApiGetAdminEmailStatisticsByType({
        endDate,
        granularity,
        platform,
        startDate,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

// Applicants whose email now belongs to a registered user, by signup date.
// cohort_total is the denominator these counts belong to: every imported
// applicant matching the filters, including the converted ones the list hides.
export type PotentialApplicantsConverted = {
  cohort_total: number;
  total: number;
  this_month: number;
  this_week: number;
  today: number;
};

export type PotentialApplicantsCounts = {
  total_count?: number;
  converted?: PotentialApplicantsConverted;
};

export const getPotentialApplicants = createAsyncThunk(
  'post/get-potential-applicants',
  async (
    { payload }: { payload: GetPotentialApplicantsBody },
    { rejectWithValue, signal },
  ) => {
    try {
      const res = await (
        await apiClient.getUserApi()
      ).userApiGetPotentialApplicants(
        { getPotentialApplicantsBody: payload },
        { signal },
      );
      // total_count / converted_count aren't in the generated client yet; widen
      // the response until the next npm run update-spec pulls them in.
      return res.data as GetPotentialApplicantsResponse &
        PotentialApplicantsCounts;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export type PotentialApplicantsUploadSummary = {
  received: number;
  added: number;
  already_present: number;
  duplicate_in_file: number;
  missing_email: number;
  moved_to_converted: number;
};

export const createPotentialApplicants = createAsyncThunk(
  'post/create-potential-applicants',
  async (payload: CreatePotentialApplicantsBody, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getUserApi()
      ).userApiCreatePotentialApplicants({
        createPotentialApplicantsBody: payload,
      });
      // The upload summary isn't in the generated client yet; widen the
      // response until the next npm run update-spec pulls it in.
      return res.data as SuccessResponseBase &
        Partial<PotentialApplicantsUploadSummary>;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const getPotentialApplicantLists = createAsyncThunk(
  'get/potential-applicant-lists',
  async (_: void, { rejectWithValue, signal }) => {
    try {
      const res = await (
        await apiClient.getUserApi()
      ).userApiGetPotentialApplicantLists({ signal });
      return res.data.lists ?? [];
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response?.data);
    }
  },
);

export const createPotentialApplicantList = createAsyncThunk(
  'post/create-potential-applicant-list',
  async ({ name }: { name: string }, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getUserApi()
      ).userApiCreatePotentialApplicantList({
        createPotentialApplicantListBody: { name },
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);

export const importPotentialApplicantListMembers = createAsyncThunk(
  'post/import-potential-applicant-list-members',
  async (
    {
      listId,
      body,
    }: { listId: string; body: ImportPotentialApplicantListMembersBody },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getUserApi()
      ).userApiImportPotentialApplicantListMembers({
        listId,
        importPotentialApplicantListMembersBody: body,
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);

export const deletePotentialApplicantList = createAsyncThunk(
  'delete/potential-applicant-list',
  async ({ listId }: { listId: string }, { rejectWithValue }) => {
    try {
      await (
        await apiClient.getUserApi()
      ).userApiDeletePotentialApplicantList({ listId });
      return listId;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response?.data);
    }
  },
);

export const getPotentialApplicantsCourseSpecializations = createAsyncThunk(
  'get/potential-applicant/specialization/list',
  async (
    {
      orderByMaxCount,
      page,
      pageSize,
      searchQuery,
    }: {
      orderByMaxCount?: boolean;
      page?: number;
      pageSize?: number;
      searchQuery?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getUserApi()
      ).userApiGetPotentialApplicantsCourseSpecializations({
        orderByMaxCount,
        page,
        pageSize,
        searchQuery,
      });
      return res.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const getPotentialApplicantsJobRoles = createAsyncThunk(
  'post/potential-applicant/job-role/list',
  async (
    {
      orderByMaxCount,
      page,
      pageSize,
      searchQuery,
    }: {
      orderByMaxCount?: boolean;
      page?: number;
      pageSize?: number;
      searchQuery?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getUserApi()
      ).userApiGetPotentialApplicantsJobRoles({
        orderByMaxCount,
        page,
        pageSize,
        searchQuery,
      });
      return res.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const getLastAppliedPotentialApplicantJobRoles = createAsyncThunk(
  'post/last-applied-potential-applicant/job-role/list',
  async (
    {
      searchQuery,
    }: {
      searchQuery?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getUserApi()
      ).userApiLastAppliedPotentialApplicantJobRoles({ searchQuery });
      return res.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);
export const getPotentialApplicantFacet = createAsyncThunk(
  'get/potential-applicant/facet/list',
  async (
    { field, searchQuery }: { field: string; searchQuery?: string },
    { rejectWithValue },
  ) => {
    try {
      return await apiClient.potentialApplicantFacet(field, searchQuery ?? '');
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAllCoupons = createAsyncThunk(
  'get/coupon-admin',
  async (
    {
      page,
      pageSize,
      searchQuery,
    }: { page: number; pageSize: number; searchQuery?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getCouponApi()
      ).couponApiGetCouponList({ page, pageSize, query: searchQuery });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getCouponById = createAsyncThunk(
  'get/coupon-admin-by-id',
  async ({ couponId }: { couponId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getCouponApi()
      ).couponApiGetCouponById({ couponId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const createCoupon = createAsyncThunk(
  'post/create-coupon',
  async (
    {
      body,
    }: {
      body: CreateCouponBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getCouponApi()
      ).couponApiCreateCoupon({ createCouponBody: body });
      enqueueSnackbar('Badge Created Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const updateCoupon = createAsyncThunk(
  'put/update-coupon',
  async (
    {
      body,
      couponId,
    }: {
      couponId: string;
      body: CreateCouponBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getCouponApi()
      ).couponApiUpdateCouponById({ couponId, createCouponBody: body });
      enqueueSnackbar('Coupon Updated Successfully', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response.data);
    }
  },
);
export const deleteCoupon = createAsyncThunk(
  'delete/coupon',
  async ({ couponId }: { couponId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getCouponApi()
      ).couponApiDeleteCouponById({ couponId });
      enqueueSnackbar('Coupon Deleted Successfully', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err.response.data);
    }
  },
);
export const getChatLogsAdmin = createAsyncThunk(
  'get/chat-logs-admin',
  async (
    {
      page,
      pageSize,
      searchQuery,
    }: {
      page?: number;
      pageSize?: number;
      searchQuery?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiGetChatLogsEmployerApplicant({ page, pageSize, searchQuery });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getActivityLogsForAdmin = createAsyncThunk(
  'get/activity-for-admin',
  async (
    {
      activityTypeList,
      applicantId,
      jobIds,
      maxDate,
      minDate,
      orgIds,
      pageNo,
      pageSize,
      userId,
      userType,
    }: {
      activityTypeList?: Array<string>;
      maxDate?: string;
      minDate?: string;
      userId?: string;
      userType?: string;
      pageNo?: number;
      pageSize?: number;
      applicantId?: string;
      jobIds?: Array<string>;
      orgIds?: Array<string>;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetSystemActivityLogs({
        activityTypeList,
        applicantId,
        jobIds,
        maxDate,
        minDate,
        orgIds,
        pageNo,
        pageSize,
        userId,
        userType,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAuditLogsForAdmin = createAsyncThunk(
  'get/audit-logs-for-admin',
  async (
    {
      actorUserId,
      method,
      action,
      entityType,
      changesOnly,
      minDate,
      maxDate,
      pageNo,
      pageSize,
    }: {
      actorUserId?: string;
      method?: string;
      action?: string;
      entityType?: string;
      changesOnly?: boolean;
      minDate?: string;
      maxDate?: string;
      pageNo?: number;
      pageSize?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getAuditLogApi()
      ).auditLogApiGetAdminAuditLogs({
        actorUserId,
        method,
        action,
        entityType,
        changesOnly,
        minDate,
        maxDate,
        pageNo,
        pageSize,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAuditLogEntityTypesForAdmin = createAsyncThunk(
  'get/audit-log-entity-types-for-admin',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.getAuditLogEntityTypes();
      return response.entity_types;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getActivityTypeCountsForAdmin = createAsyncThunk(
  'get/activity-type-counts-for-admin',
  async ({ days }: { days?: number }, { rejectWithValue }) => {
    try {
      const response = await apiClient.getActivityTypeCounts(days);
      return response.activity_types;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const getGemsActivityForAdmin = createAsyncThunk(
  'get/gems-activity-for-admin',
  async (
    {
      activityTypeList,
      createdFrom,
      createdTo,
      maxGems,
      minGems,
      page,
      pageSize,
      searchQuery,
      sortBy,
      sortOrder,
      totalPages,
    }: {
      activityTypeList?: Array<string>;
      createdFrom?: string;
      createdTo?: string;
      maxGems?: number;
      minGems?: number;
      page?: number;
      pageSize?: number;
      searchQuery?: string;
      sortBy?: string;
      sortOrder?: string;
      totalPages?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiGetGemsActivity({
        gemsActivityParam: {
          activity_type_list: activityTypeList,
          created_from: createdFrom,
          created_to: createdTo,
          max_gems: maxGems,
          min_gems: minGems,
          page,
          page_size: pageSize,
          search_query: searchQuery,
          sort_by: sortBy,
          sort_order: sortOrder,
          total_pages: totalPages,
        },
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getPotentialRecruiterJobRoleCategories = createAsyncThunk(
  'post/potential-recruiter/job-role-categories/list',
  async (
    {
      orderByMaxCount,
      page,
      pageSize,
      searchQuery,
    }: {
      orderByMaxCount?: boolean;
      page?: number;
      pageSize?: number;
      searchQuery?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getUserApi()
      ).userApiGetPotentialRecruitersJobPostedCategories({
        orderByMaxCount,
        page,
        pageSize,
        searchQuery,
      });
      return res.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const downloadPotentialRecruiterMetaData = createAsyncThunk(
  'get/PotentialRecruiterGoogleAdsMetadataCsv',
  async (
    {
      job_role_categories,
      created_after,
      created_before,
      min_last_job_posted,
      max_last_job_posted,
    }: {
      job_role_categories?: string[];
      created_after?: string;
      created_before?: string;
      min_last_job_posted?: string;
      max_last_job_posted?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiGetPotentialRecruiterGoogleAdsMetadataCsv({
        getPotentialRecruiterCsvParam: {
          job_role_categories,
          created_after,
          created_before,
          min_last_job_posted,
          max_last_job_posted,
        },
      });

      const csvContent = response.data as unknown as string;

      const blob = new Blob([csvContent], {
        type: 'text/csv;charset=utf-8;',
      });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', 'potential_recruiter_google_metadata.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return csvContent;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const acceptJobReferralVerification = createAsyncThunk(
  'admin/feedback/referral/accept',
  async ({ feedbackId }: { feedbackId: string }, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getGeneralApi()
      ).generalApiAcceptJobReferralVerificationRequest({ feedbackId });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const uploadCompetitionImage = createAsyncThunk(
  'post/competition-img',
  async (
    {
      competitionImageFile,
      competitionHeaderFile,
      mobileHeaderFile,
    }: {
      competitionImageFile?: File;
      competitionHeaderFile?: File;
      mobileHeaderFile?: File;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getCompetitionApi()
      ).competitionApiUploadCompetitionImages(
        { competitionImageFile, competitionHeaderFile, mobileHeaderFile },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('loginToken')}`,
          },
        },
      );
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response?.data);
    }
  },
);

export const updateEmailGroupAttributes = createAsyncThunk(
  'update/email-group-attributes',
  async (
    {
      email_group_id,
      body,
    }: { email_group_id: string; body: UpdateEmailGroupAttributesParam },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getMailAPi()
      ).mailApiUpdateEmailGroupAttributes({
        emailGroupId: email_group_id,
        updateEmailGroupAttributesParam: body,
      });
      enqueueSnackbar('Emails Attributes updated successfully', {
        variant: 'success',
      });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteEmailGroup = createAsyncThunk(
  'delete/email-group',
  async (
    { email_group_id }: { email_group_id: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getMailAPi()
      ).mailApiDeleteEmailGroup({ emailGroupId: email_group_id });
      enqueueSnackbar('Emails Deleted Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getEmailSettings = createAsyncThunk(
  'admin/getEmailSettings',
  async (
    {
      payload,
    }: {
      payload: {
        pageNo: number;
        pageSize: number;
        id?: string;
        searchQuery?: string;
      };
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const res = await (
        await apiClient.getMailAPi()
      ).mailApiGetEmailSettings(
        {
          id: payload.id,
          pageNo: payload.pageNo,
          pageSize: payload.pageSize,
          searchQuery: payload.searchQuery,
        },
        { signal },
      );

      return res.data as GetEmailSettingsResponse;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const approveEmailGroup = createAsyncThunk(
  'approve/email-group',
  async (
    { email_group_id }: { email_group_id: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getMailAPi()
      ).mailApiUpdateEmailGroupApproval({
        emailGroupId: email_group_id,
        updateEmailGroupApprovalParam: { is_approval_pending: false },
      });
      enqueueSnackbar('Emails Approved Successfully', { variant: 'success' });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const updateEmailSettings = createAsyncThunk(
  'admin/updateEmailSettings',
  async (
    {
      email_setting_id,
      payload,
    }: {
      email_setting_id: string;
      payload: UpdateEmailSettingsBody;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const res = await (
        await apiClient.getMailAPi()
      ).mailApiUpdateEmailSettings(
        { emailSettingId: email_setting_id, updateEmailSettingsBody: payload },
        { signal },
      );
      enqueueSnackbar('Email Settings Updated Successfully', {
        variant: 'success',
      });

      return res.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const getReferralSummaryAdmin = createAsyncThunk(
  'get/referral-summary-admin',
  async (
    {
      page,
      pageSize,
      filters,
      sortBy,
      sortOrder,
    }: {
      page: number;
      pageSize: number;
      filters?: ReferralSummaryFilters;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiGetReferralSummary({
        createdFrom: filters?.createdFrom,
        createdTo: filters?.createdTo,
        lastReferredFrom: filters?.lastReferredFrom,
        lastReferredTo: filters?.lastReferredTo,
        page,
        pageSize,
        sortBy,
        sortOrder,
      });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const getSkillStatisticsAdmin = createAsyncThunk<
  SkillStatisticsResponse,
  {
    pageNo: number;
    pageSize: number;
    filters?: SkillStatisticsFilters;
    searchQuery?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    totalPages?: number;
  }
>(
  'get/skill-statistics-admin',
  async (
    { pageNo, pageSize, filters, searchQuery, sortBy, sortOrder, totalPages },
    { rejectWithValue },
  ) => {
    try {
      const trimmedSearchQuery = searchQuery?.trim();
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetAdminSkillAnalytics({
        maxDate: filters?.maxDate,
        minDate: filters?.minDate,
        pageNo,
        pageSize,
        searchQuery: trimmedSearchQuery || undefined,
        sortBy,
        sortOrder,
        totalPages,
      });
      return {
        data: response.data.data ?? [],
        page_no: response.data.page_no ?? pageNo,
        page_size: response.data.page_size ?? pageSize,
        total_pages: response.data.total_pages ?? totalPages ?? 0,
      };
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const getReferralActivityLogsAdmin = createAsyncThunk(
  'get/referral-activity-logs-admin',
  async (
    {
      userId,
      startDate,
      endDate,
      page,
      pageSize,
    }: {
      userId: string;
      startDate?: string;
      endDate?: string;
      page?: number;
      pageSize?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiGetReferralActivityLogs({
        userId,
        endDate,
        page,
        pageSize,
        startDate,
      });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);
export const applyToJobFromAdmin = createAsyncThunk(
  'get/apply-job-admin',
  async (
    { jobId, userId }: { jobId: string; userId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiAdminCreateJobApplicant({
        jobId,
        adminJobApplyBody: { user_id: userId },
      });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);
export type SkillListFilters = {
  sortBy?:
    | 'mapped_to_child_skills'
    | 'created_at'
    | 'modified_on'
    | 'question_count'
    | 'name'
    | 'under_review_count'
    | 'not_validated_count'
    | 'subskill_count'
    | 'human_created_count'
    | 'validated_count';
  sortOrder?: 'asc' | 'desc';
  minQuestionCount?: number;
  maxQuestionCount?: number;
  minMappedCount?: number;
  maxMappedCount?: number;
  hasUnmapped?: boolean;
  missingIcon?: boolean;
  isSkillTreeAvailable?: boolean;
  isCurrentlyGenerating?: boolean;
  isPublicListed?: boolean;
  isGuidedCourse?: boolean;
};

export const getSkillTypeLists = createAsyncThunk(
  'get/skill-type',
  async (
    {
      page,
      pageSize,
      searchQuery,
      skillType,
      parentId,
      skipStore = false,
      filters,
    }: {
      skillType: string;
      page: number;
      pageSize: number;
      parentId?: string;
      searchQuery?: string;
      skipStore?: boolean;
      filters?: SkillListFilters;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetAdminSkillListBySkillType(
        {
          skillType,
          hasUnmapped: filters?.hasUnmapped ?? undefined,
          isCurrentlyGenerating: filters?.isCurrentlyGenerating ?? undefined,
          isPublicListed: filters?.isPublicListed ?? undefined,
          isSkillTreeAvailable: filters?.isSkillTreeAvailable ?? undefined,
          maxMappedCount: filters?.maxMappedCount ?? undefined,
          maxQuestionCount: filters?.maxQuestionCount ?? undefined,
          minMappedCount: filters?.minMappedCount ?? undefined,
          minQuestionCount: filters?.minQuestionCount ?? undefined,
          missingIcon: filters?.missingIcon ?? undefined,
          pageNo: page,
          pageSize,
          parentSkillId: parentId,
          searchText: searchQuery,
          sortBy: filters?.sortBy ?? undefined,
          sortOrder: filters?.sortOrder ?? undefined,
        },
        // TODO(update-spec): after the backend `is_guided_course` filter ships,
        // run `npm run update-spec` and move this into the typed request object
        // above. Until the client is regenerated it's sent as an extra axios
        // query param (merged via ...options in the generated method).
        filters?.isGuidedCourse != null
          ? { params: { is_guided_course: filters.isGuidedCourse } }
          : undefined,
      );
      return {
        data: response.data,
        skipStore,
      };
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);
export const createBulkDomain = createAsyncThunk(
  'post/create-bulk-domain',
  async ({ names }: { names: string[] }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiBulkCreateDomainsRoute({ nameList: { names } });

      enqueueSnackbar('Domains Created Successfully', {
        variant: 'success',
      });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);
export const createBulkModule = createAsyncThunk(
  'post/create-bulk-module',
  async (
    { id, names }: { id: string; names: string[] },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiBulkCreateModulesRoute({ iDNames: { id, names } });

      enqueueSnackbar('Modules Created Successfully', {
        variant: 'success',
      });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);
export const createBulkTopicSubtopic = createAsyncThunk(
  'post/create-bulk-topic-subtopic',
  async (
    {
      domainId,
      moduleId,
      mapping,
      replace,
      imageDensityEstimate,
    }: {
      domainId: string;
      moduleId: string;
      mapping?: {
        [key: string]: { [key: string]: Array<string> };
      } | null;
      replace?: boolean;
      imageDensityEstimate?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiBulkCreateTopicSubtopicRoute({
        createTopicsSubtopicsBody: {
          domain_id: domainId,
          module_id: moduleId,
          topic_subtopic_mapping: mapping,
          replace,
          image_density_estimate: imageDensityEstimate,
        },
      });

      enqueueSnackbar('Topics Sub-Topics Created Successfully', {
        variant: 'success',
      });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);
export const assignDomainToQuestion = createAsyncThunk(
  'post/assign-questions-domain',
  async (
    { limit, skillIds }: { limit: number; skillIds: string[] },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiAssignDomainsToQuestionsApi({
        iDListNumber: { n: limit, ids: skillIds },
      });

      enqueueSnackbar('Questions Assigned Successfully', {
        variant: 'success',
      });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);
export const assignModulesToQuestion = createAsyncThunk(
  'post/assign-questions-modules',
  async (
    {
      limit,
      domainId,
      skillIds,
    }: { limit: number; domainId?: string[]; skillIds?: string[] },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiAssignModulesToQuestionsApi({
        assignModulesToQuestions: {
          n: limit,
          ids: domainId,
          skill_ids: skillIds,
        },
      });

      enqueueSnackbar('Questions Assigned Successfully', {
        variant: 'success',
      });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);
export const assignTopicSubtopicToQuestion = createAsyncThunk(
  'post/assign-questions-topic-subtopic',
  async (
    {
      limit,
      domainId,
      moduleId,
    }: { limit: number; domainId: string; moduleId?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiGenerateSkillHierarchyDomainModule({
        refineByDomainModule: {
          limit,
          domain_id: domainId,
          module_id: moduleId,
        },
      });

      enqueueSnackbar('Questions Assigned Successfully', {
        variant: 'success',
      });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);
export const aiQuestionGenerationSkillHierarchy = createAsyncThunk(
  'gpt/generate-questions-skill-hierarchy',
  async (
    {
      moduleId,
      questions,
      maxQuestions,
      userProvidedPrompt,
      batchSize,
      withImages,
      imageLimitCount,
      enableDifficultyDistribution,
      model,
      problematicOnly,
      minQuestionsThreshold,
      triggerOrchestrator,
    }: {
      questions: number;
      moduleId: string;
      maxQuestions: number;
      userProvidedPrompt?: string;
      batchSize?: number;
      withImages?: boolean;
      imageLimitCount?: number | null;
      enableDifficultyDistribution?: boolean;
      model?: 'luna';
      problematicOnly?: boolean;
      minQuestionsThreshold?: number | null;
      triggerOrchestrator?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      // with_images / image_limit_count / enable_difficulty_distribution / model
      // aren't in the generated client yet; cast to any until the next
      // npm run update-spec pulls them in.
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiAiHierarchyQuestionGeneration({
        aIHierarchyQuestionGenerationBody: {
          module_id: moduleId,
          question_per_subtopic: questions,
          max_questions_per_subtopic: maxQuestions,
          user_provided_prompt: userProvidedPrompt,
          batch_size: batchSize,
          with_images: !!withImages,
          image_limit_count: withImages ? (imageLimitCount ?? null) : null,
          enable_difficulty_distribution: enableDifficultyDistribution === true,
          model: model ?? 'luna',
          problematic_only: problematicOnly ?? false,
          min_questions_threshold: minQuestionsThreshold ?? null,
          trigger_orchestrator: triggerOrchestrator,
        },
      });

      enqueueSnackbar('Questions Added Successfully', {
        variant: 'success',
      });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchSkillModuleTodos = createAsyncThunk(
  'admin/fetchSkillModuleTodos',
  async (
    {
      pageNo = 1,
      pageSize = 20,
      sortBy = 'todo_count',
      sortOrder = 'desc',
      searchText,
      step,
      domain,
    }: {
      pageNo?: number;
      pageSize?: number;
      sortBy?: 'created_at' | 'todo_count';
      sortOrder?: 'asc' | 'desc';
      searchText?: string;
      step?: string;
      domain?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetSkillModuleTodos({
        pageNo,
        pageSize,
        sortBy,
        sortOrder,
        searchText: searchText || undefined,
        step: step || undefined,
        domain: domain || undefined,
      });
      return response.data as unknown as SkillModuleTodosResponse;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const updateSkillNameAdmin = createAsyncThunk(
  'put/skill-admin',
  async (
    { skillId, newName }: { skillId: string; newName: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiRenameSkill({ name: newName, skillId });
      enqueueSnackbar('Skill Name Updated Successfully', {
        variant: 'success',
      });

      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const updateSkillCategoriesAdmin = createAsyncThunk(
  'put/skill-admin-categories',
  async (
    { skillId, categories }: { skillId: string; categories: string[] },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiUpdateSkillCategories({
        skillId,
        updateSkillCategoriesBody: { categories },
      });
      enqueueSnackbar('Skill categories updated', { variant: 'success' });
      return { skillId, categories, data: response.data };
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const updateSkillHiddenAdmin = createAsyncThunk(
  'put/skill-admin-hidden',
  async (
    { skillId, hidden }: { skillId: string; hidden: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiUpdateSkillHidden({
        skillId,
        updateSkillHiddenBody: { hidden },
      });
      return { skillId, hidden: response.data.is_hidden_by_default ?? hidden };
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const updateSkillAllowImagesAdmin = createAsyncThunk(
  'put/skill-admin-allow-images',
  async (
    {
      skillId,
      allowImages,
    }: { skillId: string; allowImages: 'default' | 'on' | 'off' },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiUpdateSkillAllowImages({
        skillId,
        updateAllowImagesBody: { allow_images: allowImages },
      });
      const data = response.data as unknown as {
        effective_allow_images?: 'default' | 'on' | 'off';
      };
      return {
        skillId,
        allowImages,
        effective: data?.effective_allow_images ?? allowImages,
      };
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);
export const deleteSkillInHeirarchy = createAsyncThunk(
  'delete/skill-admin',
  async ({ skillId }: { skillId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiDeleteUserSkillHierarchy({ skillId });
      enqueueSnackbar('Skill Deleted Successfully', {
        variant: 'success',
      });

      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);
export const getSkillCostEstimateAdmin = createAsyncThunk(
  'skill/get-cost-estimate',
  async ({ skillId }: { skillId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetSkillCostEstimate({ skillId });
      return { skillId, data: response.data };
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const recomputeSkillQualityAdmin = createAsyncThunk(
  'skill/recompute-quality',
  async ({ skillId }: { skillId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiRecomputeSkillQuality({ skillId });
      const r = response.data as any;
      const single = r?.module as
        | {
            total_q?: number;
            validated_q_total?: number;
            pool_label_count?: number;
            eligible_label_count?: number;
            image_total?: number;
            image_validated?: number;
            incorrect_pct?: number | null;
            image_quality_pct?: number | null;
          }
        | undefined;

      let msg: string;
      let variant: 'success' | 'warning' = 'success';
      if (single) {
        const totalQ = single.total_q ?? 0;
        const validated = single.validated_q_total ?? 0;
        const poolLabels = single.pool_label_count ?? 0;
        const eligibleLabels = single.eligible_label_count ?? 0;
        const imageTotal = single.image_total ?? 0;
        const imageValidated = single.image_validated ?? 0;
        const textComputed = single.incorrect_pct != null;
        const imageComputed = single.image_quality_pct != null;
        const poolOk = totalQ >= 50 && poolLabels >= 5;

        let textPart: string;
        if (textComputed) {
          textPart = 'text refreshed';
        } else if (!poolOk) {
          textPart = `not enough questions (${totalQ}/50 questions, ${poolLabels}/5 modules) — generate more`;
        } else {
          textPart = `not enough validated (${validated}/50 validated, ${eligibleLabels}/5 eligible modules) — run the validator`;
        }
        const imagePart = imageComputed
          ? `image refreshed (n=${imageValidated})`
          : imageTotal > 0
            ? `image pending (validated ${imageValidated}/20)`
            : 'no image data';
        msg = `${textPart} · ${imagePart}`;
        if (!textComputed && !imageComputed) variant = 'warning';
      } else {
        msg = `Refreshed ${r?.module_count ?? 0} module(s)`;
      }
      enqueueSnackbar(msg, { variant });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const generateImageOnlyQuestionsAdmin = createAsyncThunk(
  'skill/generate-image-only-questions',
  async (
    { skillId, target }: { skillId: string; target?: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGenerateImageOnlyQuestions({
        skillId,
        generateImageOnlyQuestionsBody: { target: target ?? 20 },
      });
      const r = response.data as any;
      const gen = r?.generated_dispatched ?? 0;
      const val = r?.validation_dispatched ?? 0;
      const msg =
        gen === 0 && val === 0
          ? 'Image pool already at target — nothing to do'
          : `Queued: ${gen} new image question${gen === 1 ? '' : 's'} to generate, ${val} existing to validate`;
      enqueueSnackbar(msg, { variant: 'success' });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const recomputeSkillImageDensityAdmin = createAsyncThunk(
  'skill/recompute-image-density',
  async ({ skillId }: { skillId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiRecomputeSkillImageDensity({ skillId });
      const { data } = response;
      if (data?.measured) {
        enqueueSnackbar(
          `Density measured: ${Number(data.pct).toFixed(1)}% (n=${data.sample_size})`,
          { variant: 'success' },
        );
      }
      // For the not-measured branch the component opens a dialog asking
      // to generate the missing batch — no snackbar here.
      return { skillId, ...(data ?? {}) };
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const generateImageDensitySampleAdmin = createAsyncThunk(
  'skill/generate-image-density-sample',
  async ({ skillId }: { skillId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGenerateImageDensitySample({ skillId });
      const { data } = response;
      const needed = data?.needed ?? 0;
      enqueueSnackbar(
        needed > 0
          ? `Queued ${needed} question${needed === 1 ? '' : 's'} for density sampling — refresh in a few minutes.`
          : 'Pool already meets the threshold.',
        { variant: 'success' },
      );
      return { skillId, ...(data ?? {}) };
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const uploadSkillLogoAdmin = createAsyncThunk(
  'skill/upload-logo',
  async (
    { skillId, iconFile }: { skillId: string; iconFile: File },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiUploadSkillImages({ skillId, iconFile });
      enqueueSnackbar('Logo updated', { variant: 'success' });
      return { skillId, data: response.data };
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const validateQuestionsAdmin = createAsyncThunk(
  'gpt/validate-questions',
  async (
    {
      limit,
      moduleId,
      strategy,
      scope,
      forceRevalidate,
    }: {
      limit: number | null | undefined;
      moduleId: string;
      strategy?: string;
      scope?: 'ai' | 'human' | 'all';
      forceRevalidate?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiValidateQuestions({
        validateQuestionBody: {
          limit: limit ?? undefined,
          module_id: moduleId,
          strategy,
          // The generated client may not yet know about `scope` / `force_revalidate`;
          // pass through anyway so apifairy reads them after the spec refresh.
          ...(scope ? { scope } : {}),
          ...(forceRevalidate ? { force_revalidate: true } : {}),
        },
      } as any);

      enqueueSnackbar('Questions Validated Successfully', {
        variant: 'success',
      });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const generateHierarchyAi = createAsyncThunk(
  'gpt/generate-hierarchy',
  async (
    {
      domain,
      module,
      userPrompt,
      previousResponse,
    }: {
      domain: string;
      module: string;
      userPrompt?: string;
      previousResponse?: { [key: string]: any } | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiAiGenerateModuleHierarchy({
        generateModuleHierarchyBody: {
          module,
          domain,
          user_prompt: userPrompt,
          previous_response: previousResponse,
        },
      });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchAppliedJobPopupData = createAsyncThunk(
  'post/applied-job-popup-data',
  async (
    {
      jobId,
      skillIds,
      userId,
    }: {
      jobId: string;
      skillIds: string[];
      userId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetAppliedJobPopupData({
        appliedJobPopupDataBody: {
          job_id: jobId,
          skill_ids: skillIds,
          user_id: userId,
        },
      });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchAllAiGenerationTasks = createAsyncThunk(
  'admin/fetchAllAiGenerationTasks',
  async (
    args: { page?: number; pageSize?: number } | undefined,
    { rejectWithValue },
  ) => {
    const page = args?.page ?? 1;
    const pageSize = args?.pageSize ?? 20;
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiGetAllAiGenerationTasks({
        page: String(page),
        pageSize: String(pageSize),
      });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const getModulePublishStatus = createAsyncThunk(
  'admin/getModulePublishStatus',
  async ({ skillId }: { skillId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetModulePublishStatus({ skillId });
      return response.data as {
        success: boolean;
        is_published: boolean;
        skill_tree_publish_date: string | null;
        ai_underreview_count: number;
      };
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

// The activity filter as the job-match request bodies spell it.
const activityBody = (activity?: ActivityFilter | null) => ({
  login_activity_mode: activity?.loginActivityMode ?? null,
  login_in_last_days: activity?.loginInLastDays ?? null,
  applied_activity_mode: activity?.appliedActivityMode ?? null,
  applied_in_last_days: activity?.appliedInLastDays ?? null,
});

// Everything the send dialog needs before an admin picks a size, for either job
// kind. The audience filters are optional and only an internal posting honours
// them — its number is the resolver's own count, so it re-runs under them; an
// external posting's is a skills-based upper bound that never saw them.
export const getJobMatchEmailPreflight = createAsyncThunk(
  'admin/getJobMatchEmailPreflight',
  async (
    {
      jobId,
      minExperience,
      maxExperience,
      studentsOnly,
      withSendable,
      exactOnly,
      hardExperience,
      activity,
      neverOpenedOnly,
      savedBand,
      withCeilings,
    }: {
      jobId: string;
      minExperience?: number | null;
      maxExperience?: number | null;
      studentsOnly?: boolean | null;
      activity?: ActivityFilter | null;
      // Skip anyone who clicked Apply on this posting.
      neverOpenedOnly?: boolean;
      // Adds sendable_now, at the cost of a slower query — the dialog asks for
      // it on a button, never on the recount that follows a keystroke.
      withSendable?: boolean;
      // Count only candidates holding a skill the employer tagged on this
      // posting, instead of the wider job-role skill net.
      exactOnly?: boolean;
      // Honour the experience band literally, dropping the candidates who never
      // stated a figure. No effect unless a band is set.
      hardExperience?: boolean;
      // With no band given, count under the one the dialog pre-fills; the
      // response names it in counted_min/max_experience.
      savedBand?: boolean;
      // False leaves out the two ceilings, which getJobMatchEmailReach serves.
      withCeilings?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiJobMatchEmailPreflight({
        jobId,
        minExperience: minExperience ?? undefined,
        maxExperience: maxExperience ?? undefined,
        studentsOnly: studentsOnly ?? undefined,
        withSendable: withSendable ?? undefined,
        exactOnly: exactOnly ?? undefined,
        hardExperience: hardExperience ?? undefined,
        loginActivityMode: activity?.loginActivityMode ?? undefined,
        loginInLastDays: activity?.loginInLastDays ?? undefined,
        appliedActivityMode: activity?.appliedActivityMode ?? undefined,
        appliedInLastDays: activity?.appliedInLastDays ?? undefined,
        neverOpenedOnly: neverOpenedOnly || undefined,
        savedBand: savedBand ?? undefined,
        withCeilings: withCeilings ?? undefined,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

// The send dialog's two filter-independent ceilings, fetched beside the
// preflight so it can open without waiting on them. No snackbar: a backend that
// predates the endpoint 404s here, and the dialog falls back to the preflight's.
export const getJobMatchEmailReach = createAsyncThunk(
  'admin/getJobMatchEmailReach',
  async ({ jobId }: { jobId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiJobMatchEmailReach({ jobId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getJobMatchEmailStats = createAsyncThunk(
  'admin/getJobMatchEmailStats',
  async ({ jobId }: { jobId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiJobMatchEmailStats({ jobId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

// The real send. `limit` may be null only for an internal posting, where it
// means "all remaining" — an external one has to build the rows that carry the
// blast, so it has no bound to work from and the backend rejects it.
export const sendJobMatchEmails = createAsyncThunk(
  'admin/sendJobMatchEmails',
  async (
    {
      jobId,
      limit,
      minExperience,
      maxExperience,
      copyToEmail,
      sendTimezone,
      studentsOnly,
      exactOnly,
      hardExperience,
      activity,
      newUserPct,
      neverSentOnly,
      neverOpenedOnly,
      listIds,
      listLimit,
    }: {
      jobId: string;
      limit: number | null;
      minExperience?: number | null;
      maxExperience?: number | null;
      copyToEmail?: string | null;
      sendTimezone?: string | null;
      studentsOnly?: boolean | null;
      exactOnly?: boolean;
      hardExperience?: boolean;
      activity?: ActivityFilter | null;
      // Share of `limit` sent to people not yet registered. Needs a `limit`.
      newUserPct?: number | null;
      // Skip anyone ever shown this posting, not just in the last 15 days.
      neverSentOnly?: boolean;
      // Skip anyone who clicked Apply on this posting.
      neverOpenedOnly?: boolean;
      // Potential-applicant lists to send to as well, up to `listLimit`
      // members. With lists, `limit: 0` sends to the lists alone.
      listIds?: string[];
      listLimit?: number | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiSendJobMatchEmails({
        jobId,
        jobMatchEmailSendBody: {
          limit,
          min_experience: minExperience ?? null,
          max_experience: maxExperience ?? null,
          copy_to_email: copyToEmail || null,
          send_timezone: sendTimezone || null,
          students_only: studentsOnly ?? null,
          exact_only: exactOnly ?? null,
          hard_experience: hardExperience ?? null,
          ...activityBody(activity),
          new_user_pct: newUserPct || null,
          never_sent_only: neverSentOnly || null,
          never_opened_only: neverOpenedOnly || null,
          list_ids: listIds?.length ? listIds : null,
          list_limit: listIds?.length ? (listLimit ?? null) : null,
        },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Renders one real match email to the logged-in admin, drawn from the same
// slice the send would target, and leaves the notification ledger alone.
export const sendJobMatchTestEmail = createAsyncThunk(
  'admin/sendJobMatchTestEmail',
  async (
    {
      jobId,
      minExperience,
      maxExperience,
      studentsOnly,
      activity,
      neverOpenedOnly,
    }: {
      jobId: string;
      minExperience?: number | null;
      maxExperience?: number | null;
      studentsOnly?: boolean | null;
      activity?: ActivityFilter | null;
      neverOpenedOnly?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiSendJobMatchTestEmail({
        jobId,
        jobMatchEmailTestBody: {
          min_experience: minExperience ?? null,
          max_experience: maxExperience ?? null,
          students_only: studentsOnly ?? null,
          ...activityBody(activity),
          never_opened_only: neverOpenedOnly || null,
        },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const activateModuleAIQuestions = createAsyncThunk(
  'admin/activateModuleAIQuestions',
  async ({ skillId }: { skillId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiBulkActionsReviewQuestions({
        bulkActionsReviewQuestionBody: {
          action: 'ACTIVATE',
          question_status: 'UnderReview',
          skill_ids: [skillId],
          is_ai_generated: true,
          dry_run: false,
        },
      });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const publishModule = createAsyncThunk(
  'admin/publishModule',
  async (
    { skillId, enable = true }: { skillId: string; enable?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiPublishModule({
        skillId,
        publishModuleBody: { enable },
      });
      enqueueSnackbar(
        enable ? 'Module published successfully' : 'Module unpublished',
        { variant: 'success' },
      );
      return response.data as {
        success: boolean;
        is_published: boolean;
        skill_tree_publish_date: string | null;
      };
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const stopAiGenerationTask = createAsyncThunk(
  'admin/stopAiGenerationTask',
  async ({ taskId }: { taskId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiStopAiQuestionGeneration({ taskId });
      enqueueSnackbar('Task stop requested successfully', {
        variant: 'success',
      });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchAllValidationRuns = createAsyncThunk(
  'admin/fetchAllValidationRuns',
  async (
    args: { page?: number; pageSize?: number } | undefined,
    { rejectWithValue },
  ) => {
    const page = args?.page ?? 1;
    const pageSize = args?.pageSize ?? 20;
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiGetAllValidationRuns({
        page: String(page),
        pageSize: String(pageSize),
      });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const stopValidationRun = createAsyncThunk(
  'admin/stopValidationRun',
  async ({ runId }: { runId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiStopValidationRun({ runId });
      enqueueSnackbar('Validation run stop requested', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);
export const getSkillHierarchy = createAsyncThunk(
  'put/jobrole-data',
  async (
    {
      skillId,
    }: {
      skillId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetSkillTree({ skillId });

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const editEmailGroupExperiment = createAsyncThunk(
  'admin/editEmailGroupExperiment',
  async (
    {
      experiment_id,
      body,
    }: { experiment_id: string; body: Record<string, unknown> },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getMailAPi()
      ).mailApiEditEmailExperiment({
        experimentId: experiment_id,
        editEmailExperimentBody:
          body as unknown as import('src/openapi').EditEmailExperimentBody,
      });
      enqueueSnackbar('Email campaign updated successfully', {
        variant: 'success',
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const pickAbWinner = createAsyncThunk(
  'admin/pickAbWinner',
  async (
    {
      experimentId,
      winnerGroupId,
    }: { experimentId: string; winnerGroupId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getMailAPi()
      ).mailApiPickAbWinner({
        experimentId,
        pickAbWinnerBody: { winner_group_id: winnerGroupId },
      });
      enqueueSnackbar('Winner picked — the rest of the audience gets it now', {
        variant: 'success',
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const excludeFromInvoiceSeriesAdmin = createAsyncThunk(
  'admin/exclude-from-invoice-series',
  async (body: AdminExcludeFromInvoiceSeriesBody, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiAdminExcludeFromInvoiceSeries({
        adminExcludeFromInvoiceSeriesBody: body,
      });
      const { data } = response;
      let summary: string;
      if (data.mode === 'adjust_financial_year') {
        summary = `Renumbered ${data.renumbered?.length ?? 0} invoice(s) into FY-reset series.`;
      } else if (data.mode === 'exclude') {
        summary = `Pushed ${data.admin_pushed?.length ?? 0} admin invoice(s); renumbered ${data.renumbered?.length ?? 0}.`;
      } else {
        summary = `Resynced ${data.resynced?.length ?? 0} invoice PDF(s).`;
      }
      enqueueSnackbar(summary, { variant: 'success' });
      return data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

// ---------- Skill builder prompt templates ----------

export const listSkillBuilderPromptTemplates = createAsyncThunk(
  'gpt/list-skill-builder-prompt-templates',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiListSkillBuilderPromptTemplates();
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getSkillBuilderPromptTemplate = createAsyncThunk(
  'gpt/get-skill-builder-prompt-template',
  async ({ templateId }: { templateId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiGetSkillBuilderPromptTemplate({ templateId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const createSkillBuilderPromptTemplate = createAsyncThunk(
  'gpt/create-skill-builder-prompt-template',
  async (
    body: {
      name: string;
      description?: string | null;
      body: string;
      parent_template_id?: string | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiCreateSkillBuilderPromptTemplate({
        skillBuilderPromptTemplateCreateBody: body,
      });
      enqueueSnackbar('Template created', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);
export const getDashboardBanners = createAsyncThunk(
  'admin/dashboard-banner/list',
  async (
    {
      page = 1,
      pageSize = 50,
      isActive,
      targetUserType,
      bannerType,
    }: {
      page?: number;
      pageSize?: number;
      isActive?: boolean;
      targetUserType?: string;
      bannerType?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getDashboardApi()
      ).dashboardApiGetDashboardBanners({
        isActive,
        page,
        pageSize,
        targetUserType,
        bannerType,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const createDashboardBanner = createAsyncThunk(
  'admin/dashboard-banner/create',
  async (body: CreateDashboardBannerBody, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getDashboardApi()
      ).dashboardApiCreateDashboardBanner({ createDashboardBannerBody: body });
      enqueueSnackbar('Banner created successfully', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);
export const updateSkillBuilderPromptTemplate = createAsyncThunk(
  'gpt/update-skill-builder-prompt-template',
  async (
    {
      templateId,
      body,
    }: {
      templateId: string;
      body: {
        name?: string | null;
        description?: string | null;
        body?: string | null;
      };
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiUpdateSkillBuilderPromptTemplate({
        templateId,
        skillBuilderPromptTemplateUpdateBody: body,
      });
      enqueueSnackbar('Template updated', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);
export const updateDashboardBanner = createAsyncThunk(
  'admin/dashboard-banner/update',
  async (
    { id, body }: { id: string; body: UpdateDashboardBannerBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getDashboardApi()
      ).dashboardApiUpdateDashboardBanner({
        bannerId: id,
        updateDashboardBannerBody: body,
      });
      enqueueSnackbar('Banner updated successfully', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);
export const deleteDashboardBanner = createAsyncThunk(
  'admin/dashboard-banner/delete',
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getDashboardApi()
      ).dashboardApiDeleteDashboardBanner({ bannerId: id });
      enqueueSnackbar('Banner deleted successfully', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const reorderDashboardBanners = createAsyncThunk(
  'admin/dashboard-banner/reorder',
  async (body: ReorderDashboardBannersBody, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getDashboardApi()
      ).dashboardApiReorderDashboardBanners({
        reorderDashboardBannersBody: body,
      });
      enqueueSnackbar('Banner order updated', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const deleteSkillBuilderPromptTemplate = createAsyncThunk(
  'gpt/delete-skill-builder-prompt-template',
  async ({ templateId }: { templateId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiDeleteSkillBuilderPromptTemplate({ templateId });
      enqueueSnackbar('Template deleted', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const uploadDashboardBannerImage = createAsyncThunk(
  'admin/dashboard-banner/upload-image',
  async ({ file }: { file: File }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getDashboardApi()
      ).dashboardApiUploadDashboardBannerImage({ imageFile: file });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const upsertSkillBuilderPromptMapping = createAsyncThunk(
  'gpt/upsert-skill-builder-prompt-mapping',
  async (
    body: { skill_id: string; template_id: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiUpsertSkillBuilderPromptMapping({
        skillBuilderPromptMappingUpsertBody: body,
      });
      enqueueSnackbar('Template assigned', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const deleteSkillBuilderPromptMapping = createAsyncThunk(
  'gpt/delete-skill-builder-prompt-mapping',
  async ({ skillId }: { skillId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiDeleteSkillBuilderPromptMapping({ skillId });
      enqueueSnackbar('Mapping cleared (will fall back to domain or default)', {
        variant: 'success',
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getSkillBuilderEffectiveTemplate = createAsyncThunk(
  'gpt/get-skill-builder-effective-template',
  async ({ skillId }: { skillId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiGetSkillBuilderEffectiveTemplate({ skillId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// ---------- Skill builder strategy templates ----------

export const listSkillBuilderStrategyTemplates = createAsyncThunk(
  'gpt/list-skill-builder-strategy-templates',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiListSkillBuilderStrategyTemplates();
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const createSkillBuilderStrategyTemplate = createAsyncThunk(
  'gpt/create-skill-builder-strategy-template',
  async (
    body: {
      name: string;
      description?: string | null;
      body: string;
      parent_template_id?: string | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiCreateSkillBuilderStrategyTemplate({
        skillBuilderStrategyTemplateCreateBody: body,
      });
      enqueueSnackbar('Strategy template created', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const upsertSkillBuilderStrategyMapping = createAsyncThunk(
  'gpt/upsert-skill-builder-strategy-mapping',
  async (
    body: { skill_id: string; template_id: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiUpsertSkillBuilderStrategyMapping({
        skillBuilderStrategyMappingUpsertBody: body,
      });
      enqueueSnackbar('Strategy assigned', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const deleteSkillBuilderStrategyMapping = createAsyncThunk(
  'gpt/delete-skill-builder-strategy-mapping',
  async ({ skillId }: { skillId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiDeleteSkillBuilderStrategyMapping({ skillId });
      enqueueSnackbar(
        'Strategy mapping cleared (will fall back to domain or default)',
        { variant: 'success' },
      );
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getSkillBuilderEffectiveStrategy = createAsyncThunk(
  'gpt/get-skill-builder-effective-strategy',
  async ({ skillId }: { skillId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiGetSkillBuilderEffectiveStrategy({ skillId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getAdminGlobalVars = createAsyncThunk(
  'admin/get-global-vars',
  async (_arg: void, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getManagementApi()
      ).managementApiGetAdminGlobalVars();
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const updateAdminGlobalVar = createAsyncThunk(
  'admin/update-global-var',
  async (
    { name, value }: { name: string; value: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getManagementApi()
      ).managementApiUpdateAdminGlobalVar({
        name,
        updateGlobalVarBody: { value },
      });
      enqueueSnackbar('Setting updated', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

const showError = (err: any) =>
  enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
    variant: 'error',
  });

/** Every free-unlock offer that hasn't been deleted (admins and sales). */
export const listFreeUnlockOffers = createAsyncThunk(
  'admin/list-free-unlock-offers',
  async (_arg: void, { rejectWithValue }) => {
    try {
      return await apiClient.listFreeUnlockOffers();
    } catch (err: any) {
      showError(err);
      return rejectWithValue(err?.response?.data);
    }
  },
);

/** Create (no offerId) or update a free-unlock offer. */
export const saveFreeUnlockOffer = createAsyncThunk(
  'admin/save-free-unlock-offer',
  async (
    { input, offerId }: { input: FreeUnlockOfferInput; offerId?: string },
    { rejectWithValue },
  ) => {
    try {
      const offer = await apiClient.saveFreeUnlockOffer(input, offerId);
      enqueueSnackbar(offerId ? 'Offer updated' : 'Offer created', {
        variant: 'success',
      });
      return offer;
    } catch (err: any) {
      showError(err);
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const endFreeUnlockOffer = createAsyncThunk(
  'admin/end-free-unlock-offer',
  async ({ offerId }: { offerId: string }, { rejectWithValue }) => {
    try {
      const offer = await apiClient.endFreeUnlockOffer(offerId);
      enqueueSnackbar('Offer ended — unlocks are back to full price', {
        variant: 'success',
      });
      return offer;
    } catch (err: any) {
      showError(err);
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const deleteFreeUnlockOffer = createAsyncThunk(
  'admin/delete-free-unlock-offer',
  async ({ offerId }: { offerId: string }, { rejectWithValue }) => {
    try {
      await apiClient.deleteFreeUnlockOffer(offerId);
      enqueueSnackbar('Offer deleted', { variant: 'success' });
      return offerId;
    } catch (err: any) {
      showError(err);
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getFreeUnlockOfferInvites = createAsyncThunk(
  'admin/get-free-unlock-offer-invites',
  async ({ offerId }: { offerId: string }, { rejectWithValue }) => {
    try {
      return await apiClient.getFreeUnlockOfferInvites(offerId);
    } catch (err: any) {
      showError(err);
      return rejectWithValue(err?.response?.data);
    }
  },
);

/** No snackbar on failure: it's a live preview, the editor just shows no number. */
export const getFreeUnlockOfferAudienceReach = createAsyncThunk(
  'admin/get-free-unlock-offer-audience-reach',
  async (
    {
      rules,
      startDate,
      offerId,
    }: { rules: FreeUnlockAudienceRules; startDate: string; offerId?: string },
    { rejectWithValue },
  ) => {
    try {
      return await apiClient.getFreeUnlockOfferAudienceReach(
        rules,
        startDate,
        offerId,
      );
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getFreeUnlockOfferEmailReach = createAsyncThunk(
  'admin/get-free-unlock-offer-email-reach',
  async (
    {
      params,
      checkDelivery,
    }: { params: FreeUnlockEmailReachParams; checkDelivery: boolean },
    { rejectWithValue },
  ) => {
    try {
      return await apiClient.getFreeUnlockOfferEmailReach(
        params,
        checkDelivery,
      );
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

const pluralOrgs = (count: number) =>
  `${count} organization${count === 1 ? '' : 's'}`;

/** Add orgs to / take them off an offer's invite list (admins and sales). */
export const changeFreeUnlockOfferInvites = createAsyncThunk(
  'admin/change-free-unlock-offer-invites',
  async (
    {
      offerId,
      offerName,
      change,
      orgIds,
    }: {
      offerId: string;
      offerName?: string;
      change: 'add' | 'remove';
      orgIds: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      const result = await apiClient.changeFreeUnlockOfferInvites(
        offerId,
        change,
        orgIds,
      );
      const count = (change === 'add' ? result.added : result.removed) ?? 0;
      const skipped = orgIds.length - count;
      const target = offerName ? `“${offerName}”` : 'the offer';
      let message = '';
      if (change === 'add') {
        message = count
          ? `Invited ${pluralOrgs(count)} to ${target}`
          : `Already invited to ${target}`;
        if (count && skipped) message += ` · ${skipped} already were`;
      } else {
        message = count
          ? `Removed ${pluralOrgs(count)} from ${target}`
          : `None of these were invited to ${target}`;
      }
      enqueueSnackbar(message, { variant: count ? 'success' : 'info' });
      return result;
    } catch (err: any) {
      showError(err);
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const updateAdminGlobalVars = createAsyncThunk(
  'admin/update-global-vars',
  async (
    { changes }: { changes: { name: string; value: string }[] },
    { rejectWithValue },
  ) => {
    try {
      const api = await apiClient.getManagementApi();
      // Sequential, and in the order given: the backend validates a composite
      // card against the rows already stored, so writes racing each other could
      // be rejected on a half-applied card.
      await changes.reduce<Promise<unknown>>(
        (chain, { name, value }) =>
          chain.then(() =>
            api.managementApiUpdateAdminGlobalVar({
              name,
              updateGlobalVarBody: { value },
            }),
          ),
        Promise.resolve(),
      );
      enqueueSnackbar('Settings updated', { variant: 'success' });
      return true;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const previewUserMails = createAsyncThunk(
  'admin/preview-user-mails',
  async (_arg: void, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getManagementApi()
      ).managementApiPreviewUserMails();
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getUserMailBatches = createAsyncThunk(
  'admin/get-user-mail-batches',
  async (params: { days?: number } | void, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getManagementApi()
      ).managementApiListUserMailBatches({ days: params?.days });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const sendUserMailTest = createAsyncThunk(
  'admin/send-user-mail-test',
  async (
    params: {
      emailId: string;
      category: string;
      courseTemplate?: string;
      competitionTemplate?: string;
      showPricing?: boolean;
      /** Job categories only: secondary blocks to render below the job CTA. */
      secondaryCategories?: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getManagementApi()
      ).managementApiSendUserMailTest({
        sendUserMailTestBody: {
          email_id: params.emailId,
          category: params.category as SendUserMailTestBodyCategoryEnum,
          course_template: params.courseTemplate as
            | SendUserMailTestBodyCourseTemplateEnum
            | undefined,
          competition_template: params.competitionTemplate as
            | SendUserMailTestBodyCompetitionTemplateEnum
            | undefined,
          show_pricing: params.showPricing,
          secondary_categories: params.secondaryCategories?.length
            ? (params.secondaryCategories as SendUserMailTestBodySecondaryCategoriesEnum[])
            : undefined,
        },
      });
      if (response.data.sent) {
        enqueueSnackbar(`Test email sent to ${params.emailId}`, {
          variant: 'success',
        });
      } else {
        // A test that built nothing is the common case for an arm with no live
        // content, so say which of the two happened rather than "failed".
        const testFailureMessage: Record<string, string> = {
          no_content_for_category:
            'No live content for that type right now — nothing to preview',
          no_sampled_user_for_template:
            'No sampled recipient fits that template right now',
        };
        enqueueSnackbar(
          testFailureMessage[response.data.reason || ''] ??
            'Nothing sent — no eligible audience to render from',
          { variant: 'warning' },
        );
      }
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const runUserMails = createAsyncThunk(
  'admin/run-user-mails',
  async (_arg: void, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getManagementApi()
      ).managementApiRunUserMails();
      if (response.data.queued) {
        enqueueSnackbar('User mail run queued', { variant: 'success' });
      } else {
        enqueueSnackbar('User mails are disabled', { variant: 'warning' });
      }
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getAdminCreditPrices = createAsyncThunk(
  'admin/get-credit-prices',
  async (_arg: void, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getCreditsApi()
      ).creditsApiGetAdminCreditPrices();
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const updateAdminCreditPrice = createAsyncThunk(
  'admin/update-credit-price',
  async (
    { category, key, value }: { category: string; key: string; value: number },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getCreditsApi()
      ).creditsApiUpdateAdminCreditPrice({
        category,
        key,
        updateCreditPriceBody: {
          value,
        },
      });
      enqueueSnackbar('Price updated', { variant: 'success' });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const adminGenerateReadingMaterial = createAsyncThunk(
  'admin/generate-reading-material',
  async (
    { skillId, modelKey }: { skillId: string; modelKey?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiAdminGenerateReadingMaterial({
        skillId,
        adminGenerateReadingMaterialBody: {
          model_key: modelKey ?? 'luna',
        },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const adminRegeneratePoorReadingMaterial = createAsyncThunk(
  'admin/regenerate-poor-reading-material',
  async (
    { skillId, modelKey }: { skillId: string; modelKey?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiAdminRegeneratePoorReadingMaterial({
        skillId,
        adminGenerateReadingMaterialBody: {
          model_key: modelKey ?? 'luna',
        },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const adminGetReadingMaterialCostEstimate = createAsyncThunk(
  'admin/reading-material-cost-estimate',
  async ({ skillId }: { skillId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiAdminGetReadingMaterialCostEstimate({ skillId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const adminGetReadingMaterialStatus = createAsyncThunk(
  'admin/reading-material-status',
  async ({ skillId }: { skillId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiAdminGetReadingMaterialStatus({ skillId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const adminGenerateMissingReadingMaterial = createAsyncThunk(
  'admin/generate-missing-reading-material',
  async (
    { skillId, modelKey }: { skillId: string; modelKey?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiAdminGenerateMissingReadingMaterial({
        skillId,
        adminGenerateReadingMaterialBody: {
          model_key: modelKey ?? 'luna',
        },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

/** Intro decks for a course's Module / Topic / SubTopic nodes. */
export const adminGetCourseIntros = createAsyncThunk(
  'admin/get-course-intros',
  async ({ moduleId }: { moduleId: string }, { rejectWithValue }) => {
    try {
      return (await apiClient.adminGetCourseIntros(moduleId)) as {
        module_id: string;
        intros: AdminIntroStatusItem[];
      };
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const adminGenerateCourseIntros = createAsyncThunk(
  'admin/generate-course-intros',
  async (
    {
      moduleId,
      modelKey,
      regenerate = false,
    }: { moduleId: string; modelKey?: string; regenerate?: boolean },
    { rejectWithValue },
  ) => {
    try {
      return (await apiClient.adminGenerateCourseIntros(moduleId, {
        model_key: modelKey ?? 'luna',
        regenerate,
      })) as { success: boolean; nodes_enqueued: number };
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getSkillGenerationPrompt = createAsyncThunk(
  'admin/get-skill-generation-prompt',
  async ({ skillId }: { skillId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetSkillGenerationPrompt({ skillId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const setSkillGenerationPrompt = createAsyncThunk(
  'admin/set-skill-generation-prompt',
  async (
    { skillId, prompt }: { skillId: string; prompt: string | null },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiSetSkillGenerationPrompt({
        skillId,
        setSkillGenerationPromptBody: { generation_prompt: prompt },
      });
      enqueueSnackbar(prompt ? 'AI guidance saved' : 'AI guidance cleared', {
        variant: 'success',
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const rebuildLabelLesson = createAsyncThunk(
  'admin/rebuild-label-lesson',
  async (
    {
      labelId,
      regenerateContent,
      questionTarget,
    }: {
      labelId: string;
      regenerateContent?: boolean;
      questionTarget?: number | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiRebuildLabel({
        labelId,
        rebuildLabelBody: {
          regenerate_content: regenerateContent ?? true,
          question_target: questionTarget ?? null,
        },
      });
      enqueueSnackbar(
        'Rebuild queued — learners keep the current lesson until the new one is ready',
        { variant: 'success' },
      );
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const adminRegenerateReadingMaterial = createAsyncThunk(
  'admin/regenerate-reading-material',
  async (
    {
      skillId,
      modelKey,
      customPrompt,
      fromScratch,
    }: {
      skillId: string;
      modelKey?: string;
      customPrompt?: string;
      fromScratch?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiAdminRegenerateReadingMaterial({
        skillId,
        adminRegenerateReadingMaterialBody: {
          model_key: modelKey ?? 'luna',
          custom_prompt: customPrompt ?? null,
          from_scratch: fromScratch ?? true,
        },
      });
      enqueueSnackbar('Regeneration queued', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const toggleGuidedCourse = createAsyncThunk(
  'admin/toggle-guided-course',
  async (
    body: {
      skillId: string;
      toggleGuidedCourseBody: ToggleGuidedCourseBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiToggleGuidedCourse(body);
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

// ── Course Generation Job ─────────────────────────────────────────────────────

export const startCourseGenerationJob = createAsyncThunk(
  'admin/startCourseGenerationJob',
  async (
    // retire_superseded_questions isn't in the generated spec yet — carried as
    // an extra field until `npm run update-spec` regenerates the client.
    body: StartCourseGenerationBody & { retire_superseded_questions?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiStartCourseGeneration({ startCourseGenerationBody: body });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getCourseGenerationJobStatus = createAsyncThunk(
  'admin/getCourseGenerationJobStatus',
  async (jobId: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiGetCourseGenerationJobStatus({ jobId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getCourseGenerationJobs = createAsyncThunk(
  'admin/getCourseGenerationJobs',
  async (
    {
      moduleId,
      limit,
      status,
    }: { moduleId?: string; status?: string; limit?: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiListCourseGenerationJobs({ limit, moduleId, status });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const cancelCourseGenerationJob = createAsyncThunk(
  'admin/cancelCourseGenerationJob',
  async (jobId: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiCancelCourseGenerationJob({ jobId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Top up only the labels below target — existing questions count so full
// labels are skipped, and existing reading material is kept.
export const courseGeneratePending = createAsyncThunk(
  'admin/courseGeneratePending',
  async (
    body: {
      moduleId: string;
      questionsPerLabel?: number;
      maxQuestionsPerLabel?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiStartCourseGenerationPending({
        startCourseGenerationBody: {
          module_id: body.moduleId,
          questions_per_label: body.questionsPerLabel,
          max_questions_per_label: body.maxQuestionsPerLabel,
        },
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Preview of what retiring a module's questions would affect (dry run) —
// supports cancellation via the thunk's abort signal for effect cleanup.
export const getCourseRetirePreview = createAsyncThunk(
  'admin/getCourseRetirePreview',
  async (
    { moduleId, jobId }: { moduleId: string; jobId?: string },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiCourseRetirePreview({ moduleId, jobId }, { signal });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Retire relative to a specific run (jobId): questions created before its
// start are superseded and removed. Omit jobId to treat every question as
// superseded.
export const courseRetire = createAsyncThunk(
  'admin/courseRetire',
  async (
    { moduleId, jobId }: { moduleId: string; jobId?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiCourseRetire({
        retireExecuteBody: { module_id: moduleId, job_id: jobId },
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getAdminCourseFeedback = createAsyncThunk(
  'admin/getAdminCourseFeedback',
  async (
    params: {
      scope: SkillApiListAdminCourseFeedbackScopeEnum;
      skill_ids?: string[];
      page?: number;
      page_size?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiListAdminCourseFeedback({
        scope: params.scope,
        skillIds: params.skill_ids,
        page: params.page,
        pageSize: params.page_size,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getAdminCourses = createAsyncThunk(
  'admin/getAdminCourses',
  async (
    params:
      | {
          q?: string;
          page?: number;
          page_size?: number;
          sort_by?: string;
          sort_order?: string;
          status?: string;
          pricing?: string;
          /** 'never': only courses no Update materials run has finished on. */
          refreshed?: 'all' | 'never';
        }
      | undefined,
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiListAdminCourses(
        {
          page: params?.page,
          pageSize: params?.page_size,
          q: params?.q,
          sortBy: params?.sort_by,
          sortOrder: params?.sort_order,
          status: params?.status,
          pricing: params?.pricing,
        },
        // `refreshed` rides as a plain query param until the typed client is
        // regenerated from the spec.
        params?.refreshed ? { params: { refreshed: params.refreshed } } : {},
      );
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const setCoursePricing = createAsyncThunk(
  'admin/setCoursePricing',
  async (
    body: {
      skillId: string;
      price_in_credits: number | null;
      credits_discount_pct: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiSetCoursePricing({
        skillId: body.skillId,
        setCoursePricingBody: {
          price_in_credits: body.price_in_credits,
          credits_discount_pct: body.credits_discount_pct,
        },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const setCourseIntroVisibility = createAsyncThunk(
  'admin/setCourseIntroVisibility',
  async (
    body: { skillId: string; showIntro: boolean },
    { rejectWithValue },
  ) => {
    try {
      return await apiClient.setCourseIntroVisibility(
        body.skillId,
        body.showIntro,
      );
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

// ── Course promotions ────────────────────────────────────────────────────────

export const getCoursePromotion = createAsyncThunk(
  'admin/getCoursePromotion',
  async ({ skillId }: { skillId: string }, { rejectWithValue }) => {
    try {
      return await apiClient.getCoursePromotion(skillId);
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const listCoursePromotions = createAsyncThunk(
  'admin/listCoursePromotions',
  async ({ skillId }: { skillId: string }, { rejectWithValue }) => {
    try {
      return await apiClient.listCoursePromotions(skillId);
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const previewCoursePromotion = createAsyncThunk(
  'admin/previewCoursePromotion',
  async (
    {
      skillId,
      duration_days,
      filters,
    }: {
      skillId: string;
      duration_days?: number;
      filters?: Record<string, unknown>;
    },
    { rejectWithValue },
  ) => {
    try {
      return await apiClient.previewCoursePromotion(skillId, {
        duration_days,
        filters,
      });
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const sendCoursePromotionTestEmail = createAsyncThunk(
  'admin/sendCoursePromotionTestEmail',
  async (
    {
      skillId,
      test_email,
      email,
    }: {
      skillId: string;
      test_email: string;
      email: PromotionEmailPayload;
    },
    { rejectWithValue },
  ) => {
    try {
      return await apiClient.sendCoursePromotionTestEmail(skillId, {
        test_email,
        email,
      });
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const createCoursePromotion = createAsyncThunk(
  'admin/createCoursePromotion',
  async (
    {
      skillId,
      duration_days,
      filters,
      email,
    }: {
      skillId: string;
      duration_days: number;
      filters?: Record<string, unknown>;
      email?: PromotionEmailPayload | null;
    },
    { rejectWithValue },
  ) => {
    try {
      return await apiClient.createCoursePromotion(skillId, {
        duration_days,
        filters,
        email,
      });
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const cancelCoursePromotion = createAsyncThunk(
  'admin/cancelCoursePromotion',
  async (
    { skillId, promotionId }: { skillId: string; promotionId: string },
    { rejectWithValue },
  ) => {
    try {
      return await apiClient.cancelCoursePromotion(skillId, promotionId);
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getUserFullJson = createAsyncThunk(
  'admin/user-full-json',
  async (
    {
      userId,
    }: {
      userId: string;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiGetUserFullJson({ userId }, { signal });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getUserResumes = createAsyncThunk(
  'admin/get-user-resumes',
  async (
    {
      userId,
    }: {
      userId: string;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await (
        await apiClient.getUserApi()
      ).userApiGetUserResumes({ userId }, { signal });
      // Route isn't in the spec, so the generated client types data as void.
      const data = response.data as unknown as UserResumeList;
      return data?.user_resumes ?? [];
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

// ── Reading-material audio narration (Polly) ────────────────────────────────
// TODO(spec-regen): switch to typed apiClient.getSkillsApi() calls after
// `npm run update-spec` once the backend ships these routes.
export type NarrateResponse = {
  labels_enqueued: number;
  total_labels: number;
};

export type NarrationCoverage = {
  course_id: string;
  total_labels: number;
  complete_labels: number;
  total_cards: number;
  cards_with_audio: number;
  labels: {
    label_id: string;
    total: number;
    in_sync: number;
    complete: boolean;
  }[];
};

export const adminNarrateCourse = createAsyncThunk(
  'admin/narrate-course',
  async (
    {
      skillId,
      modelKey,
      missingOnly = true,
    }: { skillId: string; modelKey?: string; missingOnly?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post<NarrateResponse>(
        `${ConfigUrl.API_URL}/admin/skill/${skillId}/reading-material/narrate-course`,
        { model_key: modelKey ?? null, missing_only: missingOnly },
        {
          headers: { Authorization: `Bearer ${apiClient.apiKey}` },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const adminNarrateLabel = createAsyncThunk(
  'admin/narrate-label',
  async (
    { skillId, modelKey }: { skillId: string; modelKey?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post<NarrateResponse>(
        `${ConfigUrl.API_URL}/admin/skill/${skillId}/reading-material/narrate-label`,
        { model_key: modelKey ?? null },
        {
          headers: { Authorization: `Bearer ${apiClient.apiKey}` },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const adminGetNarrationCoverage = createAsyncThunk(
  'admin/narration-coverage',
  async ({ skillId }: { skillId: string }, { rejectWithValue }) => {
    try {
      const response = await axios.get<NarrationCoverage>(
        `${ConfigUrl.API_URL}/admin/skill/${skillId}/reading-material/narration-coverage`,
        {
          headers: { Authorization: `Bearer ${apiClient.apiKey}` },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// ─── Course Builder v2 ────────────────────────────────────────────────────────

export const startCourseBuild = createAsyncThunk(
  'admin/startCourseBuild',
  async (
    {
      moduleId,
      ...body
    }: {
      moduleId: string;
      label_limit?: number;
      questions_per_label?: number;
      content_model?: 'luna';
      question_model?: 'luna';
      strategy_model?: 'luna';
      max_parallel_labels?: number;
      user_prompt?: string | null;
      regenerate_existing_content?: boolean;
      with_images?: boolean;
      with_narration?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const api = await apiClient.getSkillsApi();
      const response = await api.skillApiStartCourseBuild({
        moduleId,
        startCourseBuildBody: body,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const estimateCourseBuild = createAsyncThunk(
  'admin/estimateCourseBuild',
  async (
    {
      moduleId,
      signal,
      label_limit,
      questions_per_label,
      content_model,
      question_model,
    }: {
      moduleId: string;
      label_limit?: number;
      questions_per_label?: number;
      content_model?: 'luna';
      question_model?: 'luna';
      signal?: AbortSignal;
    },
    { rejectWithValue },
  ) => {
    try {
      const api = await apiClient.getSkillsApi();
      const response = await api.skillApiEstimateCourseBuild(
        {
          moduleId,
          labelLimit: label_limit,
          questionsPerLabel: questions_per_label,
          contentModel: content_model,
          questionModel: question_model,
        },
        { signal },
      );
      return response.data;
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
        return rejectWithValue({ canceled: true });
      }
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getCourseBuildJob = createAsyncThunk(
  'admin/getCourseBuildJob',
  async (
    { jobId, signal }: { jobId: string; signal?: AbortSignal },
    { rejectWithValue },
  ) => {
    try {
      const api = await apiClient.getSkillsApi();
      const response = await api.skillApiGetCourseBuildJob(
        { jobId },
        { signal },
      );
      return response.data;
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
        return rejectWithValue({ canceled: true });
      }
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const cancelCourseBuildJob = createAsyncThunk(
  'admin/cancelCourseBuildJob',
  async ({ jobId }: { jobId: string }, { rejectWithValue }) => {
    try {
      const api = await apiClient.getSkillsApi();
      const response = await api.skillApiCancelCourseBuildJob({ jobId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const retryCourseBuildLabel = createAsyncThunk(
  'admin/retryCourseBuildLabel',
  async ({ labelTaskId }: { labelTaskId: string }, { rejectWithValue }) => {
    try {
      const api = await apiClient.getSkillsApi();
      const response = await api.skillApiRetryCourseBuildLabel({ labelTaskId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getCourseBuildReadiness = createAsyncThunk(
  'admin/getCourseBuildReadiness',
  async ({ moduleId }: { moduleId: string }, { rejectWithValue }) => {
    try {
      const api = await apiClient.getSkillsApi();
      const response = await api.skillApiCourseBuildReadiness({ moduleId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const listCourseBuildJobs = createAsyncThunk(
  'admin/listCourseBuildJobs',
  async (
    {
      moduleId,
      page,
      pageSize,
      status,
    }: {
      // Omitted on the dashboard, which lists builds across every module.
      moduleId?: string;
      page?: number;
      pageSize?: number;
      status?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const api = await apiClient.getSkillsApi();
      const response = await api.skillApiListCourseBuildJobs({
        moduleId,
        page,
        pageSize,
        status,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

/** Restart one stopped build, or every stopped build when jobId is omitted. */
export const continueCourseBuilds = createAsyncThunk(
  'admin/continueCourseBuilds',
  async ({ jobId }: { jobId?: string }, { rejectWithValue }) => {
    try {
      return jobId
        ? await apiClient.continueCourseBuild(jobId)
        : await apiClient.continueStalledCourseBuilds();
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const reviewHierarchyOrder = createAsyncThunk(
  'admin/reviewHierarchyOrder',
  async (
    {
      topics,
      moduleName,
      userPrompt,
    }: {
      topics: CourseBuildHierarchyResponse['topics'];
      moduleName?: string;
      userPrompt?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const api = await apiClient.getSkillsApi();
      const response = await api.skillApiReviewHierarchyOrder({
        reviewHierarchyOrderBody: {
          topics: topics ?? [],
          module_name: moduleName,
          user_prompt: userPrompt,
        },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getCourseBuildHierarchy = createAsyncThunk(
  'admin/getCourseBuildHierarchy',
  async ({ moduleId }: { moduleId: string }, { rejectWithValue }) => {
    try {
      const api = await apiClient.getSkillsApi();
      const response = await api.skillApiGetCourseBuildHierarchy({ moduleId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getPremiumPromoPreflight = createAsyncThunk(
  'admin/getPremiumPromoPreflight',
  async (
    params: {
      itemId?: string;
      minLogins?: number | null;
      loginWithinDays?: number | null;
      maxCredits?: number | null;
      offerHours?: number | null;
      discountPercents?: string | null;
    },
    { rejectWithValue },
  ) => {
    try {
      return await apiClient.premiumPromoPreflight({
        item_id: params.itemId,
        min_logins: params.minLogins ?? null,
        login_within_days: params.loginWithinDays ?? null,
        max_credits: params.maxCredits ?? null,
        offer_hours: params.offerHours ?? null,
        discount_percents: params.discountPercents ?? null,
      });
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const sendPremiumPromoEmail = createAsyncThunk(
  'admin/sendPremiumPromoEmail',
  async (
    params: {
      limit?: number | null;
      itemId?: string;
      minLogins?: number | null;
      loginWithinDays?: number | null;
      maxCredits?: number | null;
      offerHours?: number | null;
      copyToEmail?: string | null;
      sendTimezone?: string | null;
      // Mail only copyToEmail — one copy per variant — and touch no candidate.
      testOnly?: boolean;
      variants?: PremiumPromoVariant[];
    },
    { rejectWithValue },
  ) => {
    try {
      return await apiClient.sendPremiumPromoEmail({
        limit: params.limit ?? null,
        item_id: params.itemId,
        min_logins: params.minLogins ?? null,
        login_within_days: params.loginWithinDays ?? null,
        max_credits: params.maxCredits ?? null,
        offer_hours: params.offerHours ?? null,
        copy_to_email: params.copyToEmail || null,
        send_timezone: params.sendTimezone || null,
        test_only: params.testOnly ?? null,
        variants: params.variants,
      });
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getPremiumPromoPreview = createAsyncThunk(
  'admin/getPremiumPromoPreview',
  async (
    params: {
      itemId?: string;
      offerHours?: number | null;
      discountPercent?: number | null;
    },
    { rejectWithValue },
  ) => {
    try {
      return await apiClient.premiumPromoPreview({
        item_id: params.itemId,
        offer_hours: params.offerHours ?? null,
        discount_percent: params.discountPercent ?? null,
      });
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

/** AI-invite settings for the AI Opponents screen, with a dry run of now. */
export const getAiInviteSettings = createAsyncThunk(
  'admin/get-ai-invite-settings',
  async (_arg: void, { rejectWithValue }) => {
    try {
      return await apiClient.getAiInviteSettings();
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const updateAiInviteSetting = createAsyncThunk(
  'admin/update-ai-invite-setting',
  async (
    { name, value }: { name: string; value: string },
    { rejectWithValue },
  ) => {
    try {
      return await apiClient.updateAiInviteSetting(name, value);
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

/** One AI-invite tick, now. Sends only if sending is switched on. */
export const runAiInvitesNow = createAsyncThunk(
  'admin/run-ai-invites-now',
  async (_arg: void, { rejectWithValue }) => {
    try {
      return await apiClient.runAiInvitesNow();
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getJobEmailPreferences = createAsyncThunk(
  'admin/get-job-email-preferences',
  async (_arg: void, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetJobEmailPreferences();
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const updateJobEmailPreference = createAsyncThunk(
  'admin/update-job-email-preference',
  async (
    { name, value }: { name: string; value: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiUpdateJobEmailPreferences({
        jobEmailPreferenceUpdateBody: { name, value },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

const sourcingErrorMessage = (err: any) =>
  err.response?.data?.error || err.response?.data?.message || err.message;

export const fetchEmployerSourcingRuns = createAsyncThunk(
  'admin/fetchEmployerSourcingRuns',
  async (
    args: { page?: number; pageSize?: number } | undefined,
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiGetEmployerSourcingRuns({
        page: args?.page ?? 1,
        pageSize: args?.pageSize ?? 10,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(sourcingErrorMessage(err));
    }
  },
);

export const fetchEmployerSourcingStats = createAsyncThunk(
  'admin/fetchEmployerSourcingStats',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiGetEmployerSourcingStats();
      return response.data;
    } catch (err: any) {
      return rejectWithValue(sourcingErrorMessage(err));
    }
  },
);

export const fetchApnaMissingJobs = createAsyncThunk(
  'admin/fetchApnaMissingJobs',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiGetApnaMissingJobs();
      return response.data;
    } catch (err: any) {
      return rejectWithValue(sourcingErrorMessage(err));
    }
  },
);

export type StartEmployerSourcingRunArgs =
  | { source: 'unstop'; body: EmployerDataScraperBody }
  | { source: 'apna_jobs'; body: ApnEmployerDataScraperBody }
  | { source: 'apna_contacts'; body: ApnEmployerDataScraperV2Body }
  | { source: 'normalise'; body: RecruiterNormalisationBody };

export const startEmployerSourcingRun = createAsyncThunk(
  'admin/startEmployerSourcingRun',
  async (args: StartEmployerSourcingRunArgs, { rejectWithValue }) => {
    try {
      const api = await apiClient.getGptApi();
      let response;
      if (args.source === 'unstop') {
        response = await api.gptApiSourceEmployerData({
          employerDataScraperBody: args.body,
        });
      } else if (args.source === 'apna_jobs') {
        response = await api.gptApiSourceApnEmployerData({
          apnEmployerDataScraperBody: args.body,
        });
      } else if (args.source === 'apna_contacts') {
        response = await api.gptApiSourceApnEmployerDataV2({
          apnEmployerDataScraperV2Body: args.body,
        });
      } else {
        response = await api.gptApiStartRecruiterNormalisation({
          recruiterNormalisationBody: args.body,
        });
      }
      enqueueSnackbar('Run started', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      const errorMessage = sourcingErrorMessage(err);
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const resumeEmployerSourcingRun = createAsyncThunk(
  'admin/resumeEmployerSourcingRun',
  async (
    { runId, token }: { runId: string; token?: string | null },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiResumeEmployerSourcingRun({
        runId,
        resumeEmployerSourcingRunBody: { token },
      });
      enqueueSnackbar('Run resumed', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      const errorMessage = sourcingErrorMessage(err);
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const stopEmployerSourcingRun = createAsyncThunk(
  'admin/stopEmployerSourcingRun',
  async ({ runId }: { runId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getGptApi()
      ).gptApiStopEmployerSourcingRun({ runId });
      enqueueSnackbar('Run stopped', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      const errorMessage = sourcingErrorMessage(err);
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);
